'use client';
import { getShareUrlForUser } from '@/actions/share-actions';
import { type WorkflowResultData } from '@/actions/workflow';
import { ShareBottomSheet } from '@/components/share/ShareBottomSheet';
import SandyLoadingScreen from '@/components/survey-form/SandyLoadingScreen';
import useIsMobile from '@/components/useIsMobile';
import { copyToClipboard } from '@/util/copy-to-clipboard';
import type {
    SurveyCompletionCountKind,
    SurveyCompletionCounts,
} from '@/util/query/survey-completion-count-query';
import {
    isFreeInputQuestionType,
    sanitizeFreeTextInput,
} from '@/util/sanitize-free-text';
import {
    CallAPICompleteSurvey,
    CallAPI_ExecuteWorkflow,
} from '@/util/services/survey/api';
import type { WorkflowResultFromComplete } from '@/util/services/survey/api.type';

import {
    accumulateSurveyTrackingDuration,
    getAllStorageSurveyAnswer,
    getSurveyTrackingState,
    loadSurveyProgress,
    markSurveyAsCompleted,
    markSurveyCompletionTracking,
    markSurveyEditStartTracking,
    saveSurveyProgress,
    startSurveyTrackingSession,
    type SurveyTrackingState,
} from '@/util/survey-storage';
import { getTTL } from '@/util/ttl';
import { getBaseUrl } from '@/util/util';
import CloseIcon from '@mui/icons-material/Close';
import WestIcon from '@mui/icons-material/West';
import { Prisma } from '@prisma/main';
import dayjs from 'dayjs';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { pushGtmEvent } from '../gtm/gtm-data-layer';
import QuestionFormat, { NO_ALLERGY_MARKER } from './questions/question-format';
import { ChartResult } from './results/ChartResult';
import { ResultCardList } from './results/ResultCardList';
import ResumeDecisionContent from './resume-decision-content';
import WorkflowResultDisplay from './workflow-result-display';

type LoadingCopyVariant = 'persona' | 'avenger' | 'vitamin';

const CATEGORY_PAGE_LOADING_COPY: Record<
    LoadingCopyVariant,
    { text: string; rotatingSubTexts: string[] }
> = {
    persona: {
        text: 'Mapping your health persona',
        rotatingSubTexts: ['Mapping your health persona'],
    },
    avenger: {
        text: 'Gathering your health insights',
        rotatingSubTexts: [
            'Identifying key priorities',
            'Translating into your personalized report',
        ],
    },
    vitamin: {
        text: 'Analyzing your nutritional gaps',
        rotatingSubTexts: ['Crafting your precision blend'],
    },
};

interface CategoryPageProps {
    slug: string;
    lang?: string; // Language code (en, th, etc.)
    categoriesData: Array<{
        id: string;
        name: string;
        nameEn?: string;
        componentName?: string;
        orderIndex: number;
        isOptional?: boolean;
        mascotImageUrl?: string | null;
        mascotMobileImageUrl?: string | null;
        questions?: Array<{
            id: string;
            questionId: string;
            code: string;
            type: string;
            question: string;
            questionEn?: string;
            required: boolean;
            placeholder: boolean;
            config: any;
            validation?: any;
        }>;
    }>;
    surveyForm: any;
    showResumeContent?: boolean;
    isCompletedSurvey?: boolean; // True if survey was completed (show different resume message)
    userId?: string; // R-commerce user ID when logged in
    userEmail?: string | null; // User email from session
    user?: any; // User object with level (optional)
    restartGroupId?: string | null | undefined;
    isViewResultMode?: boolean; // True if URL has action=view-result (show result without resume dialog, even if progress exists)
    onResumeDecisionMade?: () => void;
    isInitializing?: boolean;
    onRestartSurvey?: () => void | Promise<void>;
    // askLogin overlay (forwarded to ResumeDecisionContent for guest/anonymous users on a fresh start)
    resumeDecisionMode?: 'resume' | 'askLogin';
    onSignUpFromAskLogin?: () => void;
    onContinueAsGuest?: () => void;
}

const CategoryPage = (props: CategoryPageProps) => {
    const { categoriesData, surveyForm, restartGroupId, isViewResultMode } =
        props;
    const searchParams = useSearchParams();
    const router = useRouter();
    // Capture the ?category= param present when the page first loaded.
    // Used in handleContinueFromResume() to honour explicit external navigation (e.g., Profile → optional button).
    const initialCategoryParam = useRef<string | null>(
        searchParams.get('category'),
    );
    const { t, i18n } = useTranslation();

    // Track the effective userId (from props or created guest)
    const [effectiveUserId, setEffectiveUserId] = useState<string | undefined>(
        props.userId,
    );

    // Update effectiveUserId when props.userId changes (handles late session loading)
    useEffect(() => {
        if (props.userId && props.userId !== effectiveUserId) {
            console.log(
                '🔄 [CategoryPage] Updating effectiveUserId from props:',
                props.userId,
            );
            setEffectiveUserId(props.userId);
        }
    }, [props.userId]);

    // console.log("surveyForm:", surveyForm);

    const CATEGORY_ICON_MAP = [
        'https://dlzhyrs75lk5e.cloudfront.net/customer-cra/cms/galleryComponent/2025/11/5/690ac1e1b09f24eb4e4f6d01',
        'https://dlzhyrs75lk5e.cloudfront.net/customer-cra/cms/galleryComponent/2025/11/5/690ac1ecb09f24eb4e4f6d07',
        'https://dlzhyrs75lk5e.cloudfront.net/customer-cra/cms/galleryComponent/2025/11/5/690ac1fab09f24eb4e4f6d0d',
        'https://dlzhyrs75lk5e.cloudfront.net/customer-cra/cms/galleryComponent/2025/11/5/690ac21bb09f24eb4e4f6d19',
        'https://dlzhyrs75lk5e.cloudfront.net/customer-cra/cms/galleryComponent/2025/11/5/690ac353b09f24eb4e4f6d31',
        'https://dlzhyrs75lk5e.cloudfront.net/customer-cra/cms/galleryComponent/2025/11/5/690ac22db09f24eb4e4f6d1f',
        'https://dlzhyrs75lk5e.cloudfront.net/customer-cra/cms/galleryComponent/2025/11/5/690ac23fb09f24eb4e4f6d25',
        'https://dlzhyrs75lk5e.cloudfront.net/customer-cra/cms/galleryComponent/2025/11/5/690ac251b09f24eb4e4f6d2b',
        'https://dlzhyrs75lk5e.cloudfront.net/customer-cra/cms/article/2026/2/18/69958c834c518b31b23bc8aa',
    ];

    const ordered = useMemo(
        () =>
            [...categoriesData]
                .sort((a, b) => a.orderIndex - b.orderIndex)
                .map((cat, idx) => ({
                    ...cat,
                    iconUrl: CATEGORY_ICON_MAP[idx],
                })),
        [categoriesData],
    );

    const lastCategoryId = ordered[ordered.length - 1]?.id;

    const activeCategoryId = isViewResultMode
        ? lastCategoryId
        : searchParams.get('category') || ordered[0]?.id;
    const optionalCategory = ordered.find((category) => category.isOptional);
    const isRequiredQuestion = (
        category: CategoryPageProps['categoriesData'][number],
        question: NonNullable<
            CategoryPageProps['categoriesData'][number]['questions']
        >[number],
    ) =>
        !category.isOptional &&
        (question.required === true || question.config?.isRequired === true);
    const hasAnsweredQuestion = (
        question: NonNullable<
            CategoryPageProps['categoriesData'][number]['questions']
        >[number],
        answer?: { values: string[] },
    ) => {
        const values = answer?.values || [];
        if (values.length === 0) {
            return false;
        }

        if (
            question.type === 'text' ||
            question.type === 'email' ||
            question.type === 'date'
        ) {
            return (values[0] || '').trim() !== '';
        }

        if (question.type === 'matrix' || question.type === 'slider') {
            return Boolean(values[0]);
        }

        return values.some((value) => value !== '');
    };
    const nonOptionalCategories = ordered.filter(
        (category) => !category.isOptional,
    );
    const midpoint = Math.ceil(nonOptionalCategories.length / 2);
    const left = nonOptionalCategories.slice(0, midpoint);
    const right = nonOptionalCategories.slice(midpoint);
    const activeCategory =
        ordered.find((c) => c.id === activeCategoryId) || ordered[0];
    const activeIndex = ordered.findIndex((c) => c.id === activeCategory?.id);

    type Mode = 'categoryIntro' | 'question' | 'result';
    const [mode, setMode] = useState<Mode>(
        isViewResultMode ? 'result' : 'categoryIntro',
    );
    type QuestionItem = {
        questionText: string;
        questionType:
            | 'multiselect'
            | 'text'
            | 'radio'
            | 'email'
            | 'date'
            | 'slider'
            | 'matrix'
            | 'checkbox'
            | 'dropdown'
            | 'consent';
        questionConfig: any;
        required?: boolean;
    };

    // Helper to normalize locale to "th" or "en"
    const getLocale = (): 'th' | 'en' => {
        const lang =
            (i18n &&
                (i18n.language || (i18n.languages && i18n.languages[0]))) ||
            props.lang ||
            'th';
        if (typeof lang === 'string' && lang.toLowerCase().startsWith('en')) {
            return 'en';
        }
        return 'th';
    };

    // Helper to get text by language. If i18n language is Thai (startsWith 'th'), prefer Thai `name`/`question`.
    const getText = (
        th: string | undefined,
        en: string | undefined,
    ): string => {
        const locale = getLocale();
        if (locale === 'th') {
            return th || en || '';
        }
        return en || th || '';
    };

    const getCategoryQuestions = (
        catIndex: number,
    ): Array<QuestionItem> | null => {
        const category = ordered[catIndex];
        if (!category) return null;
        if (category.questions && category.questions.length > 0) {
            return category.questions.map((q) => ({
                questionText: getText(q.question, q.questionEn),
                questionType: q.type as any,
                questionConfig: q.config || {},
                required: q.required,
            }));
        }
        return null;
    };
    const activeQuestions = getCategoryQuestions(activeIndex);
    const activeTotalSteps = activeQuestions?.length || 0;
    const [stepIndex, setStepIndex] = useState(
        isViewResultMode
            ? activeTotalSteps > 0
                ? activeTotalSteps - 1
                : 0
            : 0,
    ); // Start at last step if view result mode, otherwise start at 0
    const [answers, setAnswers] = useState<
        Record<string, Array<{ values: string[] }>>
    >({});
    const [createdGuestId, setCreatedGuestId] = useState<string | null>(null);
    /** Guards the one-shot answer restore for the ?action=edit entry. */
    const editAnswersRestoredRef = useRef(false);
    const [workflowResult, setWorkflowResult] =
        useState<WorkflowResultData | null>(null);
    const [loadingWorkflowResult, setLoadingWorkflowResult] = useState(false);
    const [completeSurveyResults, setCompleteSurveyResults] = useState<
        WorkflowResultFromComplete[]
    >([]);
    const lastRehydrateAttemptKeyRef = useRef<string | null>(null);
    const workflowResultRef = useRef<WorkflowResultData | null>(null);
    const rehydrateInFlightRef = useRef(false);
    const skipToFinalResultRef = useRef(false);
    // GTM behavior tracking refs
    const questionShownAtRef = useRef<number | null>(null); // timestamp the current question became visible (for time_on_question)
    const fullCompleteSentRef = useRef(false); // fire survey_full_complete ('full') once per attempt
    const editCompleteSentRef = useRef(false); // fire survey_full_complete ('edited') once per edit round
    // Synchronous companion to tracking.completionEventSentAt. The persisted flag
    // only lands after setTracking -> useEffect, so once emitSurveyComplete awaits
    // the counter write a second call could slip past that guard and double-count.
    const completeSentRef = useRef(false); // fire survey_complete once per attempt
    // Last known lifetime completion counts, used as the GTM fallback when the
    // server read/write fails. NOT reset on Start Over — the counts are cumulative.
    const completionCountsRef = useRef<SurveyCompletionCounts>({
        completeCount: 0,
        fullCompleteCount: 0,
    });
    const [personaData, setPersonaData] = useState<any[]>([]);
    const [yourNutrientData, setYourNutrientData] = useState<any[]>([]);
    const [
        hasFetchedCompleteSurveyResults,
        setHasFetchedCompleteSurveyResults,
    ] = useState(false);
    const [hasFetchedPersonaHtml, setHasFetchedPersonaHtml] = useState(false);
    const [hasFetchedPersonaData, setHasFetchedPersonaData] = useState(false);
    const [hasFetchedNutrientData, setHasFetchedNutrientData] = useState(false);
    const [isProcessingNext, setIsProcessingNext] = useState(false); // Loading state for Next button
    const [isNavigatingToResult, setIsNavigatingToResult] = useState(false); // Loading state for "ดูสูตรเฉพาะคุณ" button
    const [personaHtmlContent, setPersonaHtmlContent] = useState<string | null>(
        null,
    ); // HTML content from WfResultConfig
    const [validationErrorMessage, setValidationErrorMessage] = useState<
        string | null
    >(null); // Validation error message
    const [attemptNumber, setAttemptNumber] = useState<number>(1); // Track current survey attempt number
    const [isEditFlow, setIsEditFlow] = useState<boolean>(
        searchParams.get('action') === 'edit',
    );
    // Once this attempt reached the final result it stays completed while being edited;
    // only a restart (new attempt) clears it. Profile pages read isCompleted from progress.
    const hasCompletedAttemptRef = useRef(false);
    // DF #111: true once the user has continued from the resume ("ทำต่อ")
    // prompt. Distinct from isEditFlow — a resume is not an edit session — but it
    // does mean previously answered categories may be revisited, which is what
    // the optional (option-type) category's navigation gates need.
    const [hasResumedSession, setHasResumedSession] = useState<boolean>(false);
    const [hasStartedEditCategory, setHasStartedEditCategory] =
        useState<boolean>(false);
    const [tracking, setTracking] = useState<SurveyTrackingState>(
        getSurveyTrackingState(),
    );
    const requiredCompletionThresholds = useMemo(
        () =>
            ordered
                .filter((category) => !category.isOptional)
                .map((category) => ({
                    categoryId: category.id,
                    threshold: (category.questions || []).reduce(
                        (highestIndex, question, questionIndex) =>
                            isRequiredQuestion(category, question)
                                ? questionIndex + 1
                                : highestIndex,
                        0,
                    ),
                })),
        [ordered],
    );
    const totalRequiredCount = useMemo(
        () =>
            ordered.reduce(
                (total, category) =>
                    total +
                    (category.questions || []).filter((question) =>
                        isRequiredQuestion(category, question),
                    ).length,
                0,
            ),
        [ordered],
    );
    const optionalQuestionEntries = useMemo(
        () =>
            ordered.flatMap((category) =>
                (category.questions || [])
                    .map((question, questionIndex) => ({
                        categoryId: category.id,
                        question,
                        questionIndex,
                        isOptional:
                            category.isOptional ||
                            !isRequiredQuestion(category, question),
                    }))
                    .filter((entry) => entry.isOptional),
            ),
        [ordered],
    );
    const totalOptionalCount = optionalQuestionEntries.length;
    const completedOptionalCount = useMemo(
        () =>
            optionalQuestionEntries.reduce((count, entry) => {
                const answer = answers[entry.categoryId]?.[entry.questionIndex];
                return (
                    count +
                    (hasAnsweredQuestion(entry.question, answer) ? 1 : 0)
                );
            }, 0),
        [answers, optionalQuestionEntries],
    );
    // Required questions across all (non-optional) categories. Counting answered
    // required questions directly (instead of step-progress vs a position threshold)
    // ensures completed_required_count can never exceed total_required_count.
    const requiredQuestionEntries = useMemo(
        () =>
            ordered.flatMap((category) =>
                (category.questions || [])
                    .map((question, questionIndex) => ({
                        categoryId: category.id,
                        question,
                        questionIndex,
                    }))
                    .filter((entry) =>
                        isRequiredQuestion(category, entry.question),
                    ),
            ),
        [ordered],
    );
    const getAnsweredRequiredCount = () =>
        requiredQuestionEntries.reduce((count, entry) => {
            const answer = answers[entry.categoryId]?.[entry.questionIndex];
            return (
                count + (hasAnsweredQuestion(entry.question, answer) ? 1 : 0)
            );
        }, 0);
    const areRequiredQuestionsCompleted = (
        stepsByCategory: Record<string, number>,
    ) =>
        requiredCompletionThresholds.every(
            (entry) =>
                entry.threshold === 0 ||
                (stepsByCategory[entry.categoryId] || 0) >= entry.threshold,
        );
    const beginSurveyVisit = (entrySource: 'start' | 'resume') => {
        const nowIso = new Date().toISOString();
        const nextTracking = startSurveyTrackingSession(
            { tracking: trackingRef.current },
            nowIso,
        );
        setTracking(nextTracking);
        emitSurveyEvent('survey_start', {
            entry_source: entrySource,
            first_started_at: nextTracking.firstStartedAt,
            started_at: nowIso,
            visit_count: nextTracking.visitCount,
            edit_count: nextTracking.editCount || 0,
            active_duration_seconds: nextTracking.activeDurationSeconds || 0,
        });
    };
    // Lifetime "times finished" counters. They live in user profile metadata under
    // their own top-level key so they survive Start Over (which deletes
    // surveyProgress[slug], tracking included). Both helpers fall back to the local
    // mirror so a server failure degrades the number instead of dropping the event.
    const bumpCompletionCount = async (
        kind: SurveyCompletionCountKind,
    ): Promise<number> => {
        const userId = getCurrentUserId();

        if (userId) {
            try {
                const { incrementSurveyCompletionCount } = await import(
                    '@/actions/survey'
                );
                const counts = await incrementSurveyCompletionCount(
                    userId,
                    props.slug,
                    kind,
                );
                if (counts) {
                    completionCountsRef.current = counts;
                    return kind === 'complete'
                        ? counts.completeCount
                        : counts.fullCompleteCount;
                }
            } catch (err) {
                // A server action is an HTTP POST: the action's own try/catch only
                // covers server-side failures, so a transport failure (offline,
                // server unreachable) rejects HERE. Swallow it and fall through to
                // the local fallback — this must never abort the caller, which
                // would drop the GTM event and leave onNextQuestion mid-flight.
                console.error(
                    '❌ Failed to persist survey completion count:',
                    err,
                );
            }
        }

        const previous = completionCountsRef.current;
        const next: SurveyCompletionCounts =
            kind === 'complete'
                ? { ...previous, completeCount: previous.completeCount + 1 }
                : {
                      ...previous,
                      fullCompleteCount: previous.fullCompleteCount + 1,
                  };
        completionCountsRef.current = next;

        return kind === 'complete'
            ? next.completeCount
            : next.fullCompleteCount;
    };
    // Read WITHOUT bumping — survey_full_complete ('edited') reports the existing
    // count because an edit round is not a new completion (edit_count covers it).
    // Floors at 1: reaching this code means at least one full completion happened,
    // even if it predates this counter (no backfill) or the read failed.
    const readFullCompleteCount = async (): Promise<number> => {
        const userId = getCurrentUserId();

        if (userId) {
            try {
                const { getSurveyCompletionCounts } = await import(
                    '@/actions/survey'
                );
                const counts = await getSurveyCompletionCounts(
                    userId,
                    props.slug,
                );
                if (counts) {
                    completionCountsRef.current = counts;
                }
            } catch (err) {
                // Transport failure — see the note in bumpCompletionCount.
                console.error('❌ Failed to read survey completion count:', err);
            }
        }

        return Math.max(1, completionCountsRef.current.fullCompleteCount);
    };
    /**
     * Run a step that overwrites the user's profile metadata WHOLESALE, and put the
     * completion counters back afterwards.
     *
     * Two writers in this flow do that: saveCategoryDataToProfile (via
     * queryUpdateSurveyResponseMetadata, which writes `metadata = { surveyResponse }`)
     * and updateUserMetadata (mode 'replace' over flattened keys). Both are shared
     * with the persona/profile surfaces, so rather than reshape them we read the
     * counters straight from the DB right before the wipe and re-apply them right
     * after. Restore takes a per-field max server-side, so it can never lower a
     * counter even if an increment lands in between.
     */
    const runPreservingCompletionCounts = async (
        userId: string,
        run: () => Promise<void>,
    ): Promise<void> => {
        if (!userId) {
            await run();
            return;
        }

        // Prefer DB truth; if the read is unavailable, the in-memory mirror is still
        // worth restoring — the server takes a per-field max, so a stale value can
        // only fail to help, never lower the stored count.
        let before = completionCountsRef.current;
        try {
            const { getSurveyCompletionCounts } = await import(
                '@/actions/survey'
            );
            before =
                (await getSurveyCompletionCounts(userId, props.slug)) ??
                completionCountsRef.current;
        } catch (err) {
            // Must not rethrow: run() is the caller's actual work, and skipping it
            // over a failed counter read would drop a profile write.
            console.error(
                '❌ Failed to read survey completion counts before metadata overwrite:',
                err,
            );
        }

        await run();

        // Nothing counted yet — no point writing an empty key.
        if (!before.completeCount && !before.fullCompleteCount) {
            return;
        }
        try {
            const { restoreSurveyCompletionCounts } = await import(
                '@/actions/survey'
            );
            await restoreSurveyCompletionCounts(userId, props.slug, before);
        } catch (err) {
            // Swallowed so a failed restore is not reported as a failure of the
            // surrounding step (e.g. the workflow catch block). Worst case this
            // attempt's increment is lost.
            console.error(
                '❌ Failed to restore survey completion counts:',
                err,
            );
        }
    };
    const emitSurveyComplete = async (
        stepsByCategory: Record<string, number>,
    ) => {
        if (
            completeSentRef.current ||
            trackingRef.current.completionEventSentAt ||
            !restartGroupId ||
            totalRequiredCount === 0
        ) {
            return;
        }
        // Claim the emit synchronously, before the first await: the persisted
        // completionEventSentAt guard above only becomes visible after
        // setTracking -> useEffect, so without this a second call reaching here
        // while the counter write is in flight would emit and count twice.
        completeSentRef.current = true;

        const nowIso = new Date().toISOString();
        const nextTracking = markSurveyCompletionTracking(
            { tracking: trackingRef.current },
            nowIso,
            'initial',
        );
        // markSurveyCompletionTracking clears lastEnteredAt. Re-arm it from now so the
        // time the user keeps spending on optional questions after finishing the required
        // ones still accrues into active_duration_seconds (e.g. for survey_full_complete).
        setTracking({ ...nextTracking, lastEnteredAt: nowIso });
        // Snapshot everything the payload needs before awaiting — trackingRef and
        // the answer-derived counts can move while the counter round trip is out.
        const completedRequiredCount = getAnsweredRequiredCount();
        const completeCount = await bumpCompletionCount('complete');
        emitSurveyEvent('survey_complete', {
            completion_state: 'initial',
            first_started_at: nextTracking.firstStartedAt,
            completed_at: nowIso,
            active_duration_seconds: nextTracking.activeDurationSeconds || 0,
            completion_span_seconds: nextTracking.completionSpanSeconds || 0,
            completion_span_days: nextTracking.completionSpanDays || 0,
            completed_required_count: completedRequiredCount,
            total_required_count: totalRequiredCount,
            completed_optional_count: completedOptionalCount,
            skipped_optional_count: Math.max(
                0,
                totalOptionalCount - completedOptionalCount,
            ),
            // Lifetime count of required-completions by this user, across attempts.
            complete_count: completeCount,
        });
    };
    // Fire once when EVERY question is answered, including all optional ones
    // (i.e. nothing left skipped) — a stronger completion signal than survey_complete.
    const emitSurveyFullComplete = async (
        stepsByCategory: Record<string, number>,
    ) => {
        if (fullCompleteSentRef.current || !restartGroupId) {
            return;
        }
        // Cross-session dedupe: once the FIRST full completion is recorded
        // (persisted to tracking so it survives the remount the edit flow goes
        // through), 'full' never fires again — later edit rounds re-fire as
        // 'edited' via emitSurveyFullCompleteAfterEdit instead. Checked BEFORE
        // flipping fullCompleteSentRef so the ref only ever means "the event
        // was actually emitted in this session".
        if (trackingRef.current.fullCompletedAt) {
            return;
        }
        const allRequiredDone = areRequiredQuestionsCompleted(stepsByCategory);
        const allOptionalDone = completedOptionalCount >= totalOptionalCount;
        if (!allRequiredDone || !allOptionalDone) {
            return;
        }
        fullCompleteSentRef.current = true;
        const nowIso = new Date().toISOString();
        const firstFullCompletedAt = nowIso;
        setTracking((prev) =>
            prev.fullCompletedAt ? prev : { ...prev, fullCompletedAt: nowIso },
        );
        // Compute duration/span the same way survey_complete does, without mutating tracking state
        const completedTracking = accumulateSurveyTrackingDuration(
            { tracking: trackingRef.current },
            nowIso,
        );
        const completionSpanSeconds = countDurationSeconds(
            completedTracking.firstStartedAt,
            nowIso,
        );
        // Snapshot before awaiting the counter round trip — trackingRef and the
        // answer-derived counts can move while the request is out.
        const completedRequiredCount = getAnsweredRequiredCount();
        const fullCompleteCount = await bumpCompletionCount('fullComplete');
        emitSurveyEvent('survey_full_complete', {
            completion_state: 'full',
            completed_at: nowIso,
            first_full_completed_at: firstFullCompletedAt,
            first_started_at: completedTracking.firstStartedAt,
            active_duration_seconds:
                completedTracking.activeDurationSeconds || 0,
            completion_span_seconds: completionSpanSeconds,
            completion_span_days:
                completionSpanSeconds > 0
                    ? Math.floor(completionSpanSeconds / 86400)
                    : 0,
            completed_required_count: completedRequiredCount,
            total_required_count: totalRequiredCount,
            completed_optional_count: completedOptionalCount,
            total_optional_count: totalOptionalCount,
            total_question_count: totalRequiredCount + totalOptionalCount,
            // Lifetime count of full completions by this user, across attempts.
            full_complete_count: fullCompleteCount,
        });
    };
    // Re-fire survey_full_complete after the user finishes EDITING an already
    // fully-completed survey. The first completion time is preserved via
    // tracking.fullCompletedAt; this event reports the new edit-completion time
    // under completion_state: 'edited'. Guarded so it only fires when the survey
    // had genuinely reached full completion before and is still fully answered.
    const emitSurveyFullCompleteAfterEdit = async (
        stepsByCategory: Record<string, number> = completedStepsPerCategory,
    ) => {
        // Dedupe: fires at most once per edit round. Checked BEFORE the
        // restartGroupId/fullCompletedAt guard so a second call (e.g. the
        // workflow-success safety net, or the later navigateToSpecificResult
        // click) is always a safe no-op once this has emitted.
        if (editCompleteSentRef.current) {
            return;
        }
        // If 'full' was already sent THIS session, fullCompletedAt was just set by
        // that same call moments ago — not by a genuinely earlier session. This
        // matters because isEditingAnswers/editSessionStartedAt can both be true
        // on a still-in-progress FIRST attempt too (e.g. the user went back to fix
        // an earlier category's answer before finishing), so they alone don't
        // prove this is a real "returning to edit an already-done survey" round.
        // fullCompleteSentRef only ever means "'full' was emitted this mount", so
        // it can't be fooled by that stale edit-mode flag.
        if (fullCompleteSentRef.current) {
            return;
        }
        if (!restartGroupId || !trackingRef.current.fullCompletedAt) {
            return;
        }
        const allRequiredDone = areRequiredQuestionsCompleted(stepsByCategory);
        const allOptionalDone = completedOptionalCount >= totalOptionalCount;
        if (!allRequiredDone || !allOptionalDone) {
            return;
        }
        editCompleteSentRef.current = true;
        const nowIso = new Date().toISOString();
        const completedTracking = accumulateSurveyTrackingDuration(
            { tracking: trackingRef.current },
            nowIso,
        );
        const completionSpanSeconds = countDurationSeconds(
            completedTracking.firstStartedAt,
            nowIso,
        );
        // Snapshot every trackingRef read BEFORE awaiting: the ref is re-synced by
        // an effect and can move while the counter read is in flight.
        const firstFullCompletedAt = trackingRef.current.fullCompletedAt;
        const editCount = trackingRef.current.editCount || 0;
        const completedRequiredCount = getAnsweredRequiredCount();
        // Read, don't bump: an edit round is not a new completion — edit_count
        // already measures it, so full_complete_count carries its current value.
        const fullCompleteCount = await readFullCompleteCount();
        emitSurveyEvent('survey_full_complete', {
            completion_state: 'edited',
            completed_at: nowIso,
            first_full_completed_at: firstFullCompletedAt,
            first_started_at: completedTracking.firstStartedAt,
            active_duration_seconds:
                completedTracking.activeDurationSeconds || 0,
            completion_span_seconds: completionSpanSeconds,
            completion_span_days:
                completionSpanSeconds > 0
                    ? Math.floor(completionSpanSeconds / 86400)
                    : 0,
            completed_required_count: completedRequiredCount,
            total_required_count: totalRequiredCount,
            completed_optional_count: completedOptionalCount,
            total_optional_count: totalOptionalCount,
            total_question_count: totalRequiredCount + totalOptionalCount,
            edit_count: editCount,
            // Unchanged by this edit round — see the read-don't-bump note above.
            full_complete_count: fullCompleteCount,
        });
    };

    const isFinalCategoryResult =
        mode === 'result' && activeIndex === ordered.length - 1;
    const [shareUrl, setShareUrl] = useState<string>('');
    const [preloadedImageBlob, setPreloadedImageBlob] = useState<Blob | null>(
        null,
    );
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [personaLabel, setPersonaLabel] = useState<string>('');
    const [isLoadingShareUrl, setIsLoadingShareUrl] = useState(false);
    const shareUrlFetchedRef = useRef(false);
    const isMobile = useIsMobile();
    const overlayLoadingCopy = isNavigatingToResult
        ? CATEGORY_PAGE_LOADING_COPY.vitamin
        : activeIndex === ordered.length - 1 &&
            stepIndex === activeTotalSteps - 1
          ? CATEGORY_PAGE_LOADING_COPY.avenger
          : CATEGORY_PAGE_LOADING_COPY.persona;
    const trackingRef = useRef<SurveyTrackingState>(tracking);

    useEffect(() => {
        trackingRef.current = tracking;
    }, [tracking]);

    const getCurrentUserId = () =>
        effectiveUserId || props.userId || createdGuestId;
    const getCurrentUserType = (): 'member' | 'guest' | 'anonymous' => {
        // Prefer user.level if available
        if (props.user && props.user.level) {
            if (
                props.user.level === 'BP' ||
                props.user.level === 'BP_VIP' ||
                props.user.level === 'BP_VVIP'
            ) {
                return 'member';
            }
            if (props.user.level === 'GUEST') {
                return 'guest';
            }
        }
        // Fallback to old logic
        const currentUserId = getCurrentUserId();
        if (currentUserId) {
            return 'guest';
        }
        return 'anonymous';
    };
    const countDurationSeconds = (
        startedAt?: string,
        endedAt?: string,
    ): number => {
        if (!startedAt || !endedAt) {
            return 0;
        }

        const startedAtMs = Date.parse(startedAt);
        const endedAtMs = Date.parse(endedAt);
        if (!Number.isFinite(startedAtMs) || !Number.isFinite(endedAtMs)) {
            return 0;
        }

        return Math.max(0, Math.round((endedAtMs - startedAtMs) / 1000));
    };
    const emitSurveyEvent = (
        eventName: string,
        payload: Record<string, unknown>,
    ) => {
        const currentTracking = trackingRef.current;
        pushGtmEvent({
            event: eventName,
            survey_slug: props.slug,
            survey_id: surveyForm?.id || '',
            restart_group_id: restartGroupId || '',
            visit_count: currentTracking.visitCount || 0,
            user_id: getCurrentUserId() || undefined,
            user_type: getCurrentUserType(),
            language: props.lang || i18n.language || 'th',
            ...payload,
        });
    };

    // Build the user's current position in the survey, reused by behavior events
    // (abandon / navigate_back / skip) so analytics can see exactly where it happened.
    const getSurveyPosition = (): Record<string, unknown> => {
        const currentCategory = ordered.find((c) => c.id === activeCategoryId);
        const currentQuestion =
            mode === 'question'
                ? currentCategory?.questions?.[stepIndex]
                : undefined;
        return {
            position_mode: mode, // categoryIntro / question / result
            category_id: activeCategoryId,
            category_name: getText(
                currentCategory?.name,
                currentCategory?.nameEn,
            ),
            category_index: activeIndex + 1,
            question_number: mode === 'question' ? stepIndex + 1 : undefined,
            question_id: currentQuestion
                ? currentQuestion.questionId || currentQuestion.id || ''
                : undefined,
            question_text: currentQuestion
                ? getText(currentQuestion.question, currentQuestion.questionEn)
                : undefined,
            question_type: currentQuestion ? currentQuestion.type : undefined,
        };
    };

    // Track how long the current question has been on screen (for time_on_question)
    useEffect(() => {
        if (mode === 'question') {
            questionShownAtRef.current = Date.now();
        }
    }, [mode, stepIndex, activeCategoryId]);

    const getOgImageUrl = () => {
        if (!shareUrl) return '';
        const executionResultId = shareUrl.split('/').pop();
        if (!executionResultId) return '';
        return `${window.location.origin}/api/og/${executionResultId}`;
    };

    const handleDownloadImage = async () => {
        if (!shareUrl) {
            toast.error(
                t('share.error_no_result', 'No result available to share'),
            );
            return;
        }

        const executionResultId = shareUrl.split('/').pop();
        if (!executionResultId) {
            toast.error(
                t('share.error_no_result', 'No result available to share'),
            );
            return;
        }

        try {
            let blob = preloadedImageBlob;
            if (!blob) {
                const ogImageUrl = `${window.location.origin}/api/og/${executionResultId}`;
                console.log('📸 Downloading OG image from:', ogImageUrl);
                const response = await fetch(ogImageUrl);
                if (!response.ok) {
                    throw new Error(
                        `Failed to fetch image: ${response.statusText}`,
                    );
                }
                blob = await response.blob();
            }

            console.log('📸 Image blob size:', blob.size, 'bytes');

            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'health-avenger-result.png';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            toast.success(
                'รูปภาพถูกบันทึกแล้ว! คุณสามารถแชร์ไปยัง Instagram Stories ได้',
            );
            setIsShareModalOpen(false);
        } catch (error: any) {
            console.error('❌ Error downloading image:', error);
            toast.error(
                t(
                    'share.error_failed',
                    'Unable to download image. Please try again.',
                ),
            );
        }
    };

    const handleCopyLink = async () => {
        if (!shareUrl) {
            toast.error(
                t('share.error_no_result', 'No result available to share'),
            );
            return;
        }

        try {
            await copyToClipboard(shareUrl);
            toast.success('คัดลอกลิงก์แล้ว!');
            setIsShareModalOpen(false);
        } catch (error) {
            console.error('❌ Error copying link:', error);
            toast.error('ไม่สามารถคัดลอกลิงก์ได้');
        }
    };

    useEffect(() => {
        workflowResultRef.current = workflowResult;
    }, [workflowResult]);

    useEffect(() => {
        if (searchParams.get('action') === 'edit') {
            setIsEditFlow(true);
        }
    }, [searchParams]);

    // Fetch persona HTML content from WfResultConfig when completeSurveyResults is available
    useEffect(() => {
        if (completeSurveyResults.length === 0) {
            if (isFinalCategoryResult && hasFetchedCompleteSurveyResults) {
                setHasFetchedPersonaHtml(true);
            }
            return;
        }

        // Find PERSONA type result with SURVEY_RESULT computedData.type
        const personaResult = completeSurveyResults.find((result) => {
            const computedData = result.computedData as any;
            return (
                computedData?.type === 'SURVEY_RESULT' &&
                result.resultType === 'PERSONA'
            );
        });

        if (!personaResult) {
            console.log(
                '⚠️ No PERSONA result with SURVEY_RESULT computedData.type found',
            );
            setHasFetchedPersonaHtml(true);
            return;
        }

        console.log('✅ Found PERSONA result:', {
            workflowId: personaResult.workflowId,
            resultCode: personaResult.resultCode,
            resultLabel: personaResult.resultLabel,
        });

        setPersonaLabel(personaResult.resultLabel || 'Health Avenger');

        // Fetch WfResultConfig htmlContent using server action
        (async () => {
            const { fetchPersonaHtmlContent } = await import(
                '@/actions/survey'
            );
            try {
                const result = await fetchPersonaHtmlContent(
                    personaResult.workflowId,
                    personaResult.resultCode,
                    props.lang,
                );

                if (result.success && result.data) {
                    setPersonaHtmlContent(result.data);
                }
            } finally {
                setHasFetchedPersonaHtml(true);
            }
        })();
    }, [
        completeSurveyResults,
        isFinalCategoryResult,
        hasFetchedCompleteSurveyResults,
        // Refetch when the site language changes so the persona HTML switches
        // between htmlContent and htmlContentEn.
        props.lang,
    ]);

    useEffect(() => {
        if (!isFinalCategoryResult) return;
        setHasFetchedCompleteSurveyResults(false);
        setHasFetchedPersonaHtml(false);
        setHasFetchedPersonaData(false);
        setHasFetchedNutrientData(false);
        setPersonaHtmlContent(null);
        setPersonaData([]);
        setYourNutrientData([]);
    }, [isFinalCategoryResult]);

    // Reset createdGuestId when restartGroupId changes (user clicked "Start Over")
    // This allows createGuestUser to be called again and reuse existing userId from cookies
    useEffect(() => {
        if (restartGroupId) {
            // Fetch the attempt number for this restart
            (async () => {
                const currentUserId = effectiveUserId || props.userId;
                if (currentUserId && surveyForm?.id && restartGroupId) {
                    try {
                        const { getSurveyAttemptNumber } = await import(
                            '@/actions/survey'
                        );
                        const attempt = await getSurveyAttemptNumber(
                            currentUserId,
                            surveyForm.id,
                            restartGroupId,
                        );
                        setAttemptNumber(attempt);
                    } catch (error) {
                        console.error(
                            '❌ Failed to fetch attempt number:',
                            error,
                        );
                    }
                }
            })();
        }
    }, [restartGroupId, effectiveUserId, props.userId, surveyForm?.id]);

    const latestRecExecutedAt = useMemo(() => {
        return completeSurveyResults
            .filter((r) =>
                Array.isArray((r.computedData as any)?.productGroups),
            )
            .sort(
                (a, b) =>
                    new Date(b.executedAt).getTime() -
                    new Date(a.executedAt).getTime(),
            )[0]
            ?.executedAt?.toString();
    }, [completeSurveyResults]);

    // Map recommendation workflow product groups to ChartResult format.
    // Priority source: completeSurveyResults -> computedData.productGroups -> productComposition.groupShortTitle/groupShortTitleEn
    // Fallback source: yourNutrientData (legacy mapping)
    const vitaminScores = useMemo(() => {
        const isBlockedGroup = (group: any) => {
            const blockStatus =
                typeof group?.blockStatus === 'string'
                    ? group.blockStatus.toUpperCase()
                    : '';
            const constraintsApplied = Array.isArray(group?.constraintsApplied)
                ? group.constraintsApplied.map((constraint: unknown) =>
                      String(constraint).toUpperCase(),
                  )
                : [];

            return (
                group?.blocked === true ||
                group?.isNotEligible === true ||
                blockStatus === 'CONTRAINDICATED' ||
                blockStatus === 'NOT_ELIGIBLE' ||
                group?.isContraindicated === true ||
                constraintsApplied.includes('PROHIBIT')
            );
        };

        const getChartScore = (group: any) => {
            if (isBlockedGroup(group)) {
                return 0;
            }

            return typeof group?.averageScore === 'number'
                ? group.averageScore
                : undefined;
        };

        const recommendationResult = completeSurveyResults
            .filter((result) =>
                Array.isArray((result.computedData as any)?.productGroups),
            )
            .sort(
                (a, b) =>
                    new Date(b.executedAt).getTime() -
                    new Date(a.executedAt).getTime(),
            )[0];

        const productGroups = (recommendationResult?.computedData as any)
            ?.productGroups as any[] | undefined;

        if (productGroups && productGroups.length > 0) {
            const compositionByGroupName = new Map<string, any>(
                productGroups
                    .filter(
                        (group) =>
                            !!group?.groupName && !!group?.productComposition,
                    )
                    .map((group) => [
                        group.groupName,
                        group.productComposition,
                    ]),
            );

            const mappedFromRecommendation = productGroups
                .map((group, index) => {
                    const composition =
                        group?.productComposition ||
                        compositionByGroupName.get(group?.groupName);
                    const blocked = isBlockedGroup(group);
                    const shortTitle =
                        props.lang === 'en'
                            ? composition?.groupShortTitleEn ||
                              composition?.groupShortTitle ||
                              group?.groupName
                            : composition?.groupShortTitle ||
                              composition?.groupShortTitleEn ||
                              group?.groupName;

                    const score = getChartScore(group);

                    return {
                        key:
                            composition?.id ||
                            group?.groupName ||
                            `product-group-${index}`,
                        title: shortTitle,
                        icon:
                            composition?.groupIconUrl || composition?.groupIcon,
                        score,
                        blocked,
                    };
                })
                .filter((item) => !!item.title);

            if (mappedFromRecommendation.length > 0) {
                console.log(
                    '✅ [category-page] vitaminScores from recommendation productGroups:',
                    mappedFromRecommendation,
                );
                return mappedFromRecommendation;
            }
        }

        if (yourNutrientData.length === 0) {
            return undefined;
        }

        console.log(
            '🔍 [category-page] yourNutrientData (fallback):',
            yourNutrientData,
        );

        const mappedFromNutrient = yourNutrientData.map((nutrient) => {
            const nutrientScore =
                typeof (nutrient as any).averageScore === 'number'
                    ? (nutrient as any).averageScore
                    : typeof nutrient.itemCounts === 'number'
                      ? nutrient.itemCounts
                      : undefined;

            return {
                key: nutrient.title,
                title: nutrient.title,
                icon: nutrient.titleIcon,
                score: nutrientScore,
            };
        });

        console.log(
            '✅ [category-page] vitaminScores from yourNutrientData fallback:',
            mappedFromNutrient,
        );
        return mappedFromNutrient;
    }, [completeSurveyResults, yourNutrientData, props.lang]);

    // If user refreshes on the result screen (or navigates back to a previous category result),
    // `workflowResult` state is lost and must be re-hydrated from DB.
    useEffect(() => {
        if (mode !== 'result') return;
        if (isViewResultMode) return;
        const currentUserId = effectiveUserId || props.userId || createdGuestId;
        if (!currentUserId || !surveyForm?.id) return;
        if (rehydrateInFlightRef.current) return;

        const rehydrateAttemptKey = `${currentUserId}:${surveyForm.id}:${activeCategoryId}`;
        if (lastRehydrateAttemptKeyRef.current === rehydrateAttemptKey) return;

        const existingCategoryId =
            workflowResultRef.current?.executionResult?.categoryId ?? null;
        // For category result pages, categoryId should match the activeCategoryId.
        if (
            workflowResultRef.current &&
            existingCategoryId === activeCategoryId
        )
            return;

        let cancelled = false;
        (async () => {
            lastRehydrateAttemptKeyRef.current = rehydrateAttemptKey;
            rehydrateInFlightRef.current = true;
            setLoadingWorkflowResult(true);
            try {
                const { fetchWorkflowResult } = await import(
                    '@/actions/workflow'
                );
                const result = await fetchWorkflowResult(
                    currentUserId,
                    surveyForm.id,
                    activeCategoryId,
                );
                if (!cancelled) {
                    // If no result exists yet in DB, avoid infinite re-fetch loops.
                    setWorkflowResult(result);
                }
            } catch (err) {
                console.error('❌ Failed to rehydrate workflow result:', err);
                if (!cancelled) setWorkflowResult(null);
            } finally {
                rehydrateInFlightRef.current = false;
                setLoadingWorkflowResult(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        mode,
        activeCategoryId,
        effectiveUserId,
        props.userId,
        createdGuestId,
        surveyForm?.id,
    ]);

    // Track completed steps (clicked "next") per category for progress bar
    // This replaces the old logic that counted answered questions
    const [completedStepsPerCategory, setCompletedStepsPerCategory] = useState<
        Record<string, number>
    >({});

    // Track which categories have been visited (initialized/opened)
    // This allows users to return to categories they've started even if they haven't answered any questions
    const [visitedCategories, setVisitedCategories] = useState<Set<string>>(
        new Set(),
    );

    // Auto-save to metadata whenever answers or progress changes
    useEffect(() => {
        const shouldPersistProgress =
            Object.keys(answers).length > 0 ||
            (tracking.visitCount || 0) > 0 ||
            (tracking.editCount || 0) > 0;

        if (!shouldPersistProgress) {
            return;
        }

        // Get effective userId (props.userId or createdGuestId)
        const currentUserId = effectiveUserId || props.userId || createdGuestId;

        // Don't save if no userId available yet
        if (!currentUserId) {
            console.log('⚠️ Cannot save progress: No userId available yet');
            return;
        }

        // Debounce saves to avoid too many writes
        const timeoutId = setTimeout(async () => {
            // Check if user has reached final result page (isLastStep && mode === 'result')
            const isFinalResultPage =
                activeIndex === ordered.length - 1 && mode === 'result';
            if (isFinalResultPage) {
                hasCompletedAttemptRef.current = true;
            }

            // Derive: true when the optional category has been fully answered
            const isCompletedWithOptional = optionalCategory
                ? (completedStepsPerCategory[optionalCategory.id] || 0) >=
                  (optionalCategory.questions?.length || 1)
                : false;

            await saveSurveyProgress(
                props.slug,
                {
                    answers,
                    completedStepsPerCategory,
                    visitedCategories: Array.from(visitedCategories), // Convert Set to Array for storage
                    currentCategoryId: activeCategoryId,
                    currentStepIndex: stepIndex,
                    currentMode: mode,
                    restartGroupId: restartGroupId || undefined,
                    isCompleted: hasCompletedAttemptRef.current,
                    isCompletedWithOptional,
                    tracking,
                },
                currentUserId,
            );
        }, 500); // Save 500ms after last change

        return () => clearTimeout(timeoutId);
    }, [
        answers,
        completedStepsPerCategory,
        visitedCategories,
        activeCategoryId,
        stepIndex,
        mode,
        props.slug,
        effectiveUserId,
        createdGuestId,
        props.userId,
        activeIndex,
        ordered.length,
        optionalCategory,
        restartGroupId,
        tracking,
    ]);

    const ensureLength = (
        list: Array<{ values: string[] }>,
        target: number,
    ) => {
        if (list.length === target) return list;
        if (list.length < target)
            return [
                ...list,
                ...Array.from({ length: target - list.length }, () => ({
                    values: [],
                })),
            ];
        return list.slice(0, target);
    };

    const getRowKey = (row: any) => row?.id || row?.value || '';

    const currentAnswers = ensureLength(
        answers[activeCategoryId] || [],
        activeTotalSteps,
    );
    const currentAnswer = currentAnswers[stepIndex];

    // Navigation history stack: each entry records a previous location
    // so the back button can restore it (including returning from category 2 intro
    // to category 1 result view in the example scenario).
    const [history, setHistory] = useState<
        Array<{ categoryId: string; mode: string; stepIndex: number }>
    >([]);

    const pushHistory = (entry: {
        categoryId: string;
        mode: string;
        stepIndex: number;
    }) => {
        setHistory((h) => [...h, entry]);
    };

    const goBack = () => {
        emitSurveyEvent('survey_navigate_back', {
            back_type: 'history',
            ...getSurveyPosition(),
        });
        setHistory((h) => {
            if (h.length === 0) return h; // nothing to do
            const copy = [...h];
            const last = copy.pop()!;
            // Restore category via URL param if category differs
            if (last.categoryId !== activeCategoryId) {
                const catIdx = ordered.findIndex(
                    (c) => c.id === last.categoryId,
                );
                if (catIdx >= 0) {
                    // update URL without adding to history
                    const params = new URLSearchParams(searchParams.toString());
                    params.set('category', last.categoryId);
                    router.replace(`?${params.toString()}`, { scroll: false });
                }
            }
            setMode(last.mode as any);
            setStepIndex(last.stepIndex);
            return copy;
        });
    };

    // Reset step index when category changes (enter intro again handled elsewhere)
    useEffect(() => {
        if (isViewResultMode) {
            setMode('result');
            setStepIndex(activeTotalSteps > 0 ? activeTotalSteps - 1 : 0);
            return;
        }
        setStepIndex(0);
    }, [activeCategoryId, isViewResultMode, activeTotalSteps]);

    // After onSkipOptionalCategory (edit mode) navigates URL to the last category,
    // this effect fires AFTER the "reset stepIndex to 0" effect above (same render, later in file).
    // React fires effects in declaration order, so this safely overrides stepIndex back to
    // activeTotalSteps-1, making isLastStep=true so the final insights (หลอดพลัง) branch renders.
    useEffect(() => {
        if (!skipToFinalResultRef.current) return;
        const lastCategory = ordered[ordered.length - 1];
        if (!lastCategory || activeCategoryId !== lastCategory.id) return;
        skipToFinalResultRef.current = false;
        setWorkflowResult(null);
        setStepIndex(activeTotalSteps > 0 ? activeTotalSteps - 1 : 0);
        setMode('result');
    }, [activeCategoryId, ordered, activeTotalSteps]);

    useEffect(() => {
        if (mode !== 'result') return;
        if (isViewResultMode) return;

        const isFinalCategory = activeIndex === ordered.length - 1;

        if (!isFinalCategory) return;

        finalSurveyHandle();

        // Get effective userId for storage operations
        const currentUserId = effectiveUserId || props.userId || createdGuestId;
        if (currentUserId) {
            getAllStorageSurveyAnswer(props.slug, currentUserId);
        }
    }, [
        mode,
        activeIndex,
        ordered.length,
        props.userId,
        effectiveUserId,
        createdGuestId,
        isViewResultMode,
    ]);

    useEffect(() => {
        if (!isViewResultMode) return;

        const surveyStartUrl = props.lang
            ? `/${props.lang}/survey-form/${props.slug}`
            : `/survey-form/${props.slug}`;

        const userId = effectiveUserId || props.userId || createdGuestId;
        if (!userId || !surveyForm?.id) {
            // No userId at all — redirect without showing any result content
            if (!userId) {
                console.warn(
                    '⚠️ [view-result] No userId found (logged out). Redirecting to survey start.',
                );
                router.replace(surveyStartUrl);
                return;
            }
            setHasFetchedCompleteSurveyResults(true);
            return;
        }

        const mapWorkflowResultToComplete = (
            item: WorkflowResultData,
        ): WorkflowResultFromComplete => ({
            workflowId: item.executionResult.workflowId || '',
            workflowCode: '',
            workflowName: '',
            success: true,
            resultLevel: item.executionResult.resultLevel || '',
            resultCode: item.executionResult.resultCode || '',
            resultLabel: item.executionResult.resultLabel || '',
            resultLabelEn: item.executionResult.resultLabelEn || '',
            resultType: item.executionResult.resultType || '',
            totalScore: item.executionResult.totalScore || 0,
            finalTags: Array.isArray(item.executionResult.finalTags)
                ? item.executionResult.finalTags
                : [],
            executionTime: 0,
            executedAt: item.executionResult.executedAt
                ? new Date(item.executionResult.executedAt)
                : new Date(),
            computedData: item.executionResult.computedData || {},
            showResultPage: true,
            resultPageConfig: null,
        });

        let cancelled = false;
        let redirected = false;
        (async () => {
            setHasFetchedCompleteSurveyResults(false);
            try {
                const { fetchAllWorkflowResults } = await import(
                    '@/actions/workflow'
                );
                const items = await fetchAllWorkflowResults(
                    userId,
                    surveyForm.id,
                );
                if (cancelled) return;

                const dedupedByWorkflowCategory = new Map<
                    string,
                    WorkflowResultData
                >();
                for (const item of items) {
                    const execution = item.executionResult;
                    const dedupeKey = `${execution.categoryId || 'survey'}:${
                        execution.workflowId
                    }`;
                    if (!dedupedByWorkflowCategory.has(dedupeKey)) {
                        dedupedByWorkflowCategory.set(dedupeKey, item);
                    }
                }

                const mapped = Array.from(dedupedByWorkflowCategory.values())
                    .sort(
                        (a, b) =>
                            new Date(
                                b.executionResult.executedAt || 0,
                            ).getTime() -
                            new Date(
                                a.executionResult.executedAt || 0,
                            ).getTime(),
                    )
                    .map(mapWorkflowResultToComplete);

                // No results found — user likely logged out with a stale guest session
                if (mapped.length === 0) {
                    console.warn(
                        '⚠️ [view-result] No workflow results found. Redirecting to survey start.',
                    );
                    redirected = true;
                    router.replace(surveyStartUrl);
                    return;
                }

                setCompleteSurveyResults(mapped);
            } catch (error) {
                console.error(
                    '❌ Failed to fetch stored workflow results in view result mode:',
                    error,
                );
                if (!cancelled) {
                    redirected = true;
                    router.replace(surveyStartUrl);
                }
            } finally {
                // Only mark as fetched if we're not redirecting — keeps the loading spinner
                // visible until navigation completes, preventing an empty result flash
                if (!cancelled && !redirected) {
                    setHasFetchedCompleteSurveyResults(true);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        isViewResultMode,
        effectiveUserId,
        props.userId,
        createdGuestId,
        surveyForm?.id,
    ]);

    // Fetch persona data when reaching last category result page
    useEffect(() => {
        if (mode !== 'result') return;

        const isFinalCategory = activeIndex === ordered.length - 1;
        if (!isFinalCategory) return;

        const userId = props.userId || createdGuestId;
        if (!userId) {
            setHasFetchedPersonaData(true);
            return;
        }

        const fetchPersonas = async () => {
            setHasFetchedPersonaData(false);
            try {
                const { fetchPersonasByUserId } = await import(
                    '@/actions/survey'
                );
                const result = await fetchPersonasByUserId(
                    userId,
                    surveyForm?.id,
                    props.lang,
                );

                if (result.success && result.data) {
                    console.log('✅ Fetched persona data:', result.data);
                    setPersonaData(result.data);
                } else {
                    console.error('❌ Failed to fetch personas:', result.error);
                }
            } catch (error) {
                console.error('❌ Error fetching personas:', error);
            } finally {
                setHasFetchedPersonaData(true);
            }
        };

        fetchPersonas();
    }, [
        mode,
        activeIndex,
        ordered.length,
        props.userId,
        createdGuestId,
        surveyForm?.id,
    ]);

    // Fetch yourNutrientData when reaching last category result page
    // Same approach as SpecificResultPage for consistency
    useEffect(() => {
        if (mode !== 'result') return;

        const isFinalCategory = activeIndex === ordered.length - 1;
        if (!isFinalCategory) return;

        const userId = props.userId || createdGuestId;
        if (!userId) {
            setHasFetchedNutrientData(true);
            return;
        }

        const fetchNutrientData = async () => {
            setHasFetchedNutrientData(false);
            try {
                const { fetchYourNutrientData } = await import(
                    '@/actions/survey'
                );
                const result = await fetchYourNutrientData(
                    userId,
                    surveyForm?.id,
                    'TH',
                );

                if (result.success && result.data) {
                    console.log('✅ Fetched yourNutrientData:', result.data);
                    setYourNutrientData(result.data);
                } else {
                    console.error(
                        '❌ Failed to fetch yourNutrientData:',
                        result.error,
                    );
                }
            } catch (error) {
                console.error('❌ Error fetching yourNutrientData:', error);
            } finally {
                setHasFetchedNutrientData(true);
            }
        };

        fetchNutrientData();
    }, [
        mode,
        activeIndex,
        ordered.length,
        props.userId,
        createdGuestId,
        surveyForm?.id,
    ]);

    const finalSurveyHandle = async (): Promise<void> => {
        console.log('🔍 [finalSurveyHandle] Called with:', {
            propsUserId: props.userId || 'NULL',
            createdGuestId: createdGuestId || 'NULL',
        });

        let userId = props.userId;
        if (!props.userId && !createdGuestId) {
            console.log(
                '⚠️ [finalSurveyHandle] No userId found, will call createGuestUser',
            );
            console.log(
                '🔍 [finalSurveyHandle] Calling createGuestUser with userId:',
                props.userId || 'NOT PROVIDED',
            );
            try {
                const { createGuestUser } = await import('@/actions/user');
                const guestUser = await createGuestUser(
                    {
                        surveySlug: props.slug,
                    },
                    props.userId, // Reuse existing userId from cookies if available
                );
                userId = guestUser?.id || '';
                setCreatedGuestId(userId);
                console.log(
                    '✅ [finalSurveyHandle] Guest user created:',
                    userId,
                );

                // Automatically log in the guest user
                if (guestUser?.customerAccount) {
                    try {
                        const { signInGuest } = await import('@/actions');
                        // Use redirect: false to prevent page navigation, but session will still be created
                        await signInGuest(
                            guestUser.customerAccount,
                            false,
                            guestUser.id,
                            'guest_survey',
                        ); // Use 'guest_survey' authFlow
                        console.log(
                            '✅ [finalSurveyHandle] Guest user logged in automatically:',
                            guestUser.customerAccount,
                        );
                    } catch (loginErr) {
                        console.error(
                            '❌ [finalSurveyHandle] Failed to login guest user:',
                            loginErr,
                        );
                    }
                }
            } catch (err) {
                console.error(
                    '❌ [finalSurveyHandle] Failed to create user at final result:',
                    err,
                );
            }
        } else {
            console.log(
                '✅ [finalSurveyHandle] userId already available:',
                props.userId || createdGuestId,
            );
        }
        const finalUserId = userId || createdGuestId;
        if (!finalUserId) {
            console.error(
                '❌ [finalSurveyHandle] No user ID available to update metadata.',
            );
            return;
        }
        // updateUserMetadata replaces the profile metadata wholesale — preserve the
        // lifetime completion counters across it.
        await runPreservingCompletionCounts(finalUserId, () =>
            updateUserMetadata(finalUserId, answers),
        );

        if (finalUserId && surveyForm?.id) {
            try {
                const completeSurveyResponse = await CallAPICompleteSurvey(
                    surveyForm.id,
                    {
                        userId: finalUserId,
                    },
                );

                if (
                    completeSurveyResponse.success &&
                    completeSurveyResponse.result
                ) {
                    setCompleteSurveyResults(
                        completeSurveyResponse.result.workflowResults,
                    );

                    const resultsToShow =
                        completeSurveyResponse.result.workflowResults.filter(
                            (result) => result.showResultPage,
                        );
                } else {
                    console.error(
                        'Survey completion API failed:',
                        completeSurveyResponse.error,
                    );
                }
            } catch (error) {
                console.error('Error calling complete survey API:', error);
            } finally {
                setHasFetchedCompleteSurveyResults(true);
            }
        } else {
            setHasFetchedCompleteSurveyResults(true);
        }
    };

    const updateUserMetadata = async (
        userId: string,
        answers: Prisma.JsonValue,
    ): Promise<void> => {
        try {
            const { updateUserProfileMetadataForUser } = await import(
                '@/actions/user'
            );
            const metadata = {
                surveyResponse: answers,
            };
            await updateUserProfileMetadataForUser(
                userId,
                metadata,
                'replace-survey-response',
            );
        } catch (err) {
            console.error('Failed to update user metadata:', err);
        }
    };

    /**
     * Save category data to user profile after category completion
     * This fetches the transformed data from SfSurveyResponse (which has structured matrix/slider data)
     * and saves it to UserProfile.metadata with the same structure
     */
    const saveCategoryDataToProfile = async (
        categoryId: string,
    ): Promise<void> => {
        console.log('🔍 [saveCategoryDataToProfile] Called with:', {
            categoryId,
            propsUserId: props.userId || 'NULL',
            createdGuestId: createdGuestId || 'NULL',
        });

        let userId = props.userId || createdGuestId;

        // If no user exists yet, create or reuse guest user
        if (!userId && !creationInFlight.current) {
            console.log(
                '⚠️ [saveCategoryDataToProfile] No userId found, will call createGuestUser',
            );
            console.log(
                '🔍 [saveCategoryDataToProfile] Calling createGuestUser with userId:',
                props.userId || 'NOT PROVIDED',
            );
            try {
                const { createGuestUser } = await import('@/actions/user');
                const guestUser = await createGuestUser(
                    {
                        surveySlug: props.slug,
                    },
                    props.userId, // Reuse existing userId from cookies if available
                );
                userId = guestUser?.id || '';
                setCreatedGuestId(userId);
                console.log(
                    '✅ [saveCategoryDataToProfile] Guest user ready for category data save:',
                    userId,
                );

                // Automatically log in the guest user
                if (guestUser?.customerAccount) {
                    try {
                        const { signInGuest } = await import('@/actions');
                        await signInGuest(
                            guestUser.customerAccount,
                            false,
                            guestUser.id,
                            'guest_survey',
                        ); // Pass userId
                        console.log(
                            '✅ [saveCategoryDataToProfile] Guest user logged in automatically:',
                            guestUser.customerAccount,
                        );
                    } catch (loginErr) {
                        console.error(
                            '❌ [saveCategoryDataToProfile] Failed to login guest user:',
                            loginErr,
                        );
                    }
                }
            } catch (err) {
                console.error(
                    '❌ [saveCategoryDataToProfile] Failed to create guest user for category save:',
                    err,
                );
                return;
            }
        } else {
            console.log(
                '✅ [saveCategoryDataToProfile] userId already available:',
                userId,
            );
        }

        if (!userId) {
            console.error('❌ No user ID available to save category data');
            return;
        }

        if (!surveyForm?.id) {
            console.error('❌ Missing surveyForm.id');
            return;
        }

        try {
            // Step 1: Fetch transformed data from SfSurveyResponse
            // This data already has the correct structure (matrix/slider transformations applied)
            const { getCategorySurveyResponses } = await import(
                '@/actions/survey'
            );
            const responseResult = await getCategorySurveyResponses(
                userId,
                surveyForm.id,
                categoryId,
            );

            if (
                !responseResult.success ||
                !responseResult.data ||
                responseResult.data.length === 0
            ) {
                console.log(
                    `ℹ️ No survey responses found for category ${categoryId}, skipping metadata save`,
                );
                return;
            }

            const transformedCategoryAnswers = responseResult.data;
            console.log(
                `📦 Fetched ${transformedCategoryAnswers.length} transformed responses from SfSurveyResponse for category ${categoryId}`,
            );

            // Step 2: Check if category data already exists in user profile
            const {
                getSurveyResponseFromProfile,
                updateSurveyResponseInProfile,
            } = await import('@/actions/user');
            const existingSurveyData =
                await getSurveyResponseFromProfile(userId);

            if (existingSurveyData && existingSurveyData[categoryId]) {
                console.log(
                    `ℹ️ Category data already exists for category ${categoryId}, updating...`,
                );
            } else {
                console.log(
                    `📝 No existing data found for category ${categoryId}, creating new entry...`,
                );
            }

            // Step 3: Save transformed data to metadata with NESTED structure (same as SfSurveyResponse)
            await updateSurveyResponseInProfile(
                userId,
                categoryId,
                transformedCategoryAnswers,
            );
            // console.log(`✅ Category data saved for category ${categoryId}, user ${userId} with structured format`);
        } catch (err) {
            console.error('❌ Failed to save category data:', err);
        }
    };

    // Ensure stored answers trimmed/expanded when question count changes for same category
    useEffect(() => {
        setAnswers((prev) => {
            const existing = prev[activeCategoryId];
            if (!existing) return prev;
            return {
                ...prev,
                [activeCategoryId]: ensureLength(existing, activeTotalSteps),
            };
        });
    }, [activeTotalSteps, activeCategoryId]);

    const updateCurrentAnswer = (newValues: string[]) => {
        // Free-input answers (short text / textarea / email) are attacker
        // controllable and are later rendered into HTML on the public share
        // page. Strip any markup at the point of capture so the stored answer
        // is plain text; the share page escapes on render as a second layer.
        const questionType = activeQuestions?.[stepIndex]?.questionType;
        const sanitizedValues = isFreeInputQuestionType(questionType)
            ? newValues.map((value) => sanitizeFreeTextInput(value))
            : newValues;

        setAnswers((prev) => {
            const list = ensureLength(
                prev[activeCategoryId] || [],
                activeTotalSteps,
            );
            const clone = list.map((v) => ({ values: [...v.values] }));
            clone[stepIndex] = { values: sanitizedValues };
            return {
                ...prev,
                [activeCategoryId]: ensureLength(clone, activeTotalSteps),
            };
        });
    };

    // Initialize slider values with min when first rendering a slider question
    useEffect(() => {
        if (mode !== 'question') return;
        const currentQuestion = activeQuestions?.[stepIndex];
        if (!currentQuestion || currentQuestion.questionType !== 'slider')
            return;
        const currentVals = currentAnswer?.values || [];
        if (currentVals.length > 0 && currentVals[0]) return;
        // DB stores slider fields under `sliders`; `fields` is the mock/legacy
        // shape. Fall back so defaults seed for real data too (see validation).
        const fields =
            currentQuestion.questionConfig?.fields ||
            currentQuestion.questionConfig?.sliders ||
            [];
        const initialValues: Record<
            string,
            { value: number; unit: string; isAnswered: boolean }
        > = {};
        fields.forEach((field: any) => {
            // ถ้า Disable Bar + Disable Min/Max → ไม่ set default value เพื่อบังคับให้ user กรอกเอง
            if (field.disableBar === true && field.disableMinMax === true)
                return;

            const normalizedUnits =
                Array.isArray(field.units) && field.units.length > 0
                    ? field.units
                    : field.unit
                      ? [{ value: field.unit, factor: 1, isDefault: true }]
                      : [];
            const defaultUnit =
                normalizedUnits.find((unit: any) => unit.isDefault)?.value ||
                normalizedUnits[0]?.value ||
                field.unit ||
                '';
            initialValues[field.name || field.id] = {
                value: field.min,
                unit: defaultUnit,
                isAnswered: false,
            };
        });
        // เรียก updateCurrentAnswer เฉพาะเมื่อมี field ที่ต้อง set default (ป้องกันการ override คำตอบที่ user กรอกไปแล้ว)
        if (Object.keys(initialValues).length > 0) {
            updateCurrentAnswer([JSON.stringify(initialValues)]);
        }
    }, [mode, stepIndex, activeCategoryId]);

    const goToCategory = (idx: number) => {
        const target = ordered[idx];
        if (!target) return;
        setWorkflowResult(null);
        setLoadingWorkflowResult(false);
        setIsProcessingNext(false);
        const params = new URLSearchParams(searchParams.toString());
        params.set('category', target.id);
        router.replace(`?${params.toString()}`, { scroll: false });
        setMode('categoryIntro');
        setStepIndex(0);
    };

    // Handle progress bar click - go back to category intro
    const handleProgressBarClick = (categoryIndex: number) => {
        console.log('🔍 Progress bar clicked:', {
            categoryIndex,
            currentMode: mode,
        });

        // Save current state to history before navigating
        if (mode !== 'categoryIntro') {
            pushHistory({
                categoryId: activeCategoryId,
                mode: mode,
                stepIndex: stepIndex,
            });
        }

        // Navigate to selected category intro
        goToCategory(categoryIndex);
    };

    const creationInFlight = useRef(false);

    const onStartCategory = async () => {
        if (creationInFlight.current) return;

        const currentUID = props?.userId || createdGuestId || undefined;

        // If user is logged in (has userId from session), just set it to state
        if (props.userId && !createdGuestId) {
            setCreatedGuestId(props.userId);
        }

        // Create or reuse guest user when starting the survey (only on first category start)
        // Check if we need to setup guest user (either new or reuse existing from cookies)
        // Check: no currentUID (not logged in and no guest created yet), first category
        if (!currentUID && activeIndex === 0) {
            creationInFlight.current = true;
            try {
                const { createGuestUser } = await import('@/actions/user');

                // Pass props.userId (from cookies) to reuse existing guest, or create new if null
                const guestUser = await createGuestUser(
                    {
                        surveySlug: props.slug,
                    },
                    currentUID, // Reuse existing userId from cookies if available
                );
                const guestUserId = guestUser?.id || '';
                setCreatedGuestId(guestUserId);

                // Automatically log in the guest user (similar to checkout flow)
                if (guestUser?.customerAccount) {
                    try {
                        const { signInGuest } = await import('@/actions');
                        await signInGuest(
                            guestUser.customerAccount,
                            false,
                            guestUser.id,
                            'guest_survey',
                        ); // Pass userId
                    } catch (loginErr) {
                        console.error(
                            '❌ [onStartCategory] Failed to login guest user:',
                            loginErr,
                        );
                    }
                } else {
                    console.error(
                        '❌ [onStartCategory] Guest user missing customerAccount, cannot login',
                    );
                }
            } catch (err) {
                console.error(
                    '❌ [onStartCategory] Failed to create guest user at survey start:',
                    err,
                );
            } finally {
                creationInFlight.current = false;
            }
        }

        pushHistory({
            categoryId: activeCategoryId,
            mode: 'categoryIntro',
            stepIndex: 0,
        });

        // Mark this category as visited when user starts it
        setVisitedCategories((prev) => new Set(prev).add(activeCategoryId));
        if (
            !isEditingAnswers &&
            activeIndex === 0 &&
            (trackingRef.current.visitCount || 0) === 0
        ) {
            beginSurveyVisit('start');
        }
        if (isEditingAnswers) {
            setHasStartedEditCategory(true);
        }

        if (activeTotalSteps > 0) {
            setMode('question');
            setStepIndex(0);
        }
    };

    const onSkipOptionalCategory = () => {
        emitSurveyEvent('survey_skip', {
            skip_target: 'optional_category',
            ...getSurveyPosition(),
        });
        // DF #111: a resumed session may jump to the final result like edit mode,
        // but ONLY once every required category is answered. The final-result page
        // persists `isCompleted: true` (see the autosave effect) and triggers
        // CallAPICompleteSurvey, so allowing this mid-survey would mark a
        // partially answered survey complete and permanently lock the user out of
        // the categories they never filled in.
        const hasAnsweredAllRequired = areRequiredQuestionsCompleted(
            completedStepsPerCategory,
        );
        const isInEditMode =
            isEditFlow ||
            searchParams.get('action') === 'edit' ||
            (props.isCompletedSurvey === true && !props.showResumeContent) ||
            (hasResumedSession && hasAnsweredAllRequired);

        if (isInEditMode) {
            // Edit mode: show the final result page (หลอดพลัง/final insights) just like
            // completing the last category normally. router.replace() is async, so we must
            // NOT call setMode/setStepIndex here — they would fire while activeCategoryId is
            // still the old category, putting us in the wrong branch of the result JSX.
            // Instead, set a flag and let the deferred useEffect call them once
            // activeCategoryId has actually updated to the last category.
            const lastCategory = ordered[ordered.length - 1];
            setVisitedCategories((prev) => new Set(prev).add(activeCategoryId));
            if (lastCategory && lastCategory.id !== activeCategoryId) {
                skipToFinalResultRef.current = true;
                const params = new URLSearchParams(searchParams.toString());
                params.set('category', lastCategory.id);
                router.replace(`?${params.toString()}`, { scroll: false });
            } else {
                // Already on last category — safe to set immediately
                setWorkflowResult(null);
                setStepIndex(activeTotalSteps > 0 ? activeTotalSteps - 1 : 0);
                setMode('result');
            }
        } else {
            // Normal mode: show the current (optional) category's own result
            pushHistory({
                categoryId: activeCategoryId,
                mode: 'categoryIntro',
                stepIndex: 0,
            });
            setVisitedCategories((prev) => new Set(prev).add(activeCategoryId));
            setMode('result');
            setStepIndex(activeTotalSteps > 0 ? activeTotalSteps - 1 : 0);
        }
    };

    /**
     * Validates the current question answer based on question type and requirements
     * Returns an object with { isValid: boolean, errorMessage?: string }
     */
    const validateCurrentQuestion = (): {
        isValid: boolean;
        errorMessage?: string;
    } => {
        const currentQuestion = activeQuestions?.[stepIndex];
        const vals = currentAnswer?.values || [];

        if (!currentQuestion) {
            return { isValid: false, errorMessage: 'No question found' };
        }

        const isRequired =
            currentQuestion.required === true ||
            currentQuestion.questionConfig?.isRequired === true;

        // Consent questions - check metadata for canProceed status
        if (currentQuestion.questionType === 'consent') {
            if (vals.length === 0) {
                return {
                    isValid: false,
                    errorMessage: t(
                        'survey_form.category_page.error_consent_required',
                    ),
                };
            }

            const checkboxes = currentQuestion.questionConfig?.checkboxes || [];

            if (checkboxes.length === 0) {
                const hasScrolled = vals.includes('__scrolled__');
                if (!hasScrolled) {
                    return {
                        isValid: false,
                        errorMessage: t(
                            'survey_form.category_page.error_must_scroll_to_bottom',
                        ),
                    };
                }
            } else {
                const requiredCheckboxIds = checkboxes
                    .filter((cb: any) => cb.isRequired)
                    .map((cb: any) => cb.id);

                const hasScrolled = vals.includes('__scrolled__');
                const actualCheckboxIds = vals.filter(
                    (v) => !v.startsWith('__'),
                );
                const allRequiredChecked =
                    requiredCheckboxIds.length === 0 ||
                    requiredCheckboxIds.every((id: string) =>
                        actualCheckboxIds.includes(id),
                    );

                if (!hasScrolled || !allRequiredChecked) {
                    return {
                        isValid: false,
                        errorMessage: t(
                            'survey_form.category_page.error_must_scroll_and_check',
                        ),
                    };
                }
            }
            return { isValid: true };
        }

        // Radio questions are ALWAYS required
        if (currentQuestion.questionType === 'radio') {
            if (vals.length === 0) {
                return {
                    isValid: false,
                    errorMessage: t(
                        'survey_form.category_page.error_answer_required',
                    ),
                };
            }
            return { isValid: true };
        }

        // Matrix questions are ALWAYS required - must fill all rows
        if (currentQuestion.questionType === 'matrix') {
            if (vals.length === 0 || !vals[0]) {
                return {
                    isValid: false,
                    errorMessage: t(
                        'survey_form.category_page.error_please_fill_all_rows',
                    ),
                };
            }
            try {
                const matrixData = JSON.parse(vals[0]);
                const rows = currentQuestion.questionConfig?.rows || [];

                // Check that all rows have a selection (value is not undefined, null, or empty string)
                const allFilled = rows.every((row: any) => {
                    const rowKey = getRowKey(row);
                    const value = matrixData[rowKey];
                    return (
                        value !== undefined && value !== null && value !== ''
                    );
                });

                if (!allFilled) {
                    return {
                        isValid: false,
                        errorMessage: t(
                            'survey_form.category_page.error_please_fill_all_rows',
                        ),
                    };
                }
            } catch (e) {
                console.error('Error parsing matrix data:', e);
                return {
                    isValid: false,
                    errorMessage: t(
                        'survey_form.category_page.error_invalid_data_format',
                    ),
                };
            }
            return { isValid: true };
        }

        // Date questions - validate date is within min/max range
        if (currentQuestion.questionType === 'date') {
            const dateValue = vals[0]?.trim() || '';

            if (!dateValue) {
                return {
                    isValid: false,
                    errorMessage: t(
                        'survey_form.category_page.error_please_select_date',
                    ),
                };
            }

            const dayjs = require('dayjs');
            const selectedDate = dayjs(dateValue);

            if (!selectedDate.isValid()) {
                return {
                    isValid: false,
                    errorMessage: t(
                        'survey_form.category_page.error_invalid_date',
                    ),
                };
            }

            if (currentQuestion.questionConfig?.disableWeekends) {
                const day = selectedDate.day();
                if (day === 0 || day === 6) {
                    return {
                        isValid: false,
                        errorMessage: t(
                            'survey_form.category_page.error_weekend_not_allowed',
                        ),
                    };
                }
            }

            let minDate = currentQuestion.questionConfig?.minDate;
            if (!minDate && currentQuestion.questionConfig?.disablePastDates) {
                minDate = dayjs().format('YYYY-MM-DD');
            }

            if (minDate) {
                const minDateObj = dayjs(minDate);
                if (selectedDate.isBefore(minDateObj, 'day')) {
                    return {
                        isValid: false,
                        errorMessage: t(
                            'survey_form.category_page.error_date_before_min',
                            { minDate },
                        ),
                    };
                }
            }

            let maxDate = currentQuestion.questionConfig?.maxDate;
            if (
                !maxDate &&
                currentQuestion.questionConfig?.disableFutureDates
            ) {
                maxDate = dayjs().format('YYYY-MM-DD');
            }

            if (maxDate) {
                const maxDateObj = dayjs(maxDate);
                if (selectedDate.isAfter(maxDateObj, 'day')) {
                    return {
                        isValid: false,
                        errorMessage: t(
                            'survey_form.category_page.error_date_after_max',
                            { maxDate },
                        ),
                    };
                }
            }

            return { isValid: true };
        }

        // Slider questions: ทุก field ต้องมีค่า (slider ถือว่า required เสมอ)
        if (currentQuestion.questionType === 'slider') {
            if (vals.length === 0 || !vals[0]) {
                return {
                    isValid: false,
                    errorMessage: t(
                        'survey_form.category_page.error_answer_required',
                    ),
                };
            }

            try {
                const sliderData = JSON.parse(vals[0]);

                // "ไม่ทราบผล" was selected — treat as answered (all fields intentionally empty)
                if (sliderData.__skipped === true) {
                    return { isValid: true };
                }

                // DB stores slider fields under `sliders`; `fields` is the
                // mock/legacy shape. Reading only `fields` yields [] for real
                // data, which makes both the required and range checks below
                // vacuously pass (every()/find() over an empty array). Fall back
                // to `sliders` like the render and save paths already do.
                const fields =
                    currentQuestion.questionConfig?.fields ||
                    currentQuestion.questionConfig?.sliders ||
                    [];

                const allFilled = fields.every((field: any) => {
                    // Key matches the save path (question-format), which stores
                    // under `name || id` — `name` alone is undefined for
                    // sliders configured with only an id.
                    const fieldKey = field.name || field.id;
                    const fieldData = sliderData[fieldKey];
                    const fieldValue = fieldData?.value;
                    return (
                        fieldValue !== undefined &&
                        fieldValue !== null &&
                        fieldValue !== ''
                    );
                });

                if (!allFilled) {
                    return {
                        isValid: false,
                        errorMessage: t(
                            'survey_form.category_page.error_answer_required',
                        ),
                    };
                }

                // Range check: a typed value can exceed the configured min/max
                // (the input only clamps the slider handle, not the raw text —
                // e.g. typing 999 on a 15–50 field). Enter/next/submit must not
                // advance with an out-of-range value. Fields explicitly opting
                // out of min/max (disableMinMax) are exempt, matching the input.
                const outOfRange = fields.find((field: any) => {
                    if (field.disableBar === true && field.disableMinMax === true)
                        return false;
                    const fieldKey = field.name || field.id;
                    const raw = sliderData[fieldKey]?.value;
                    const numericValue = Number(raw);
                    if (isNaN(numericValue)) return false;
                    if (field.min != null && numericValue < field.min)
                        return true;
                    if (field.max != null && numericValue > field.max)
                        return true;
                    return false;
                });

                if (outOfRange) {
                    return {
                        isValid: false,
                        errorMessage: t(
                            'survey_form.category_page.error_slider_out_of_range',
                            {
                                min: outOfRange.min,
                                max: outOfRange.max,
                            },
                        ),
                    };
                }
            } catch (e) {
                return {
                    isValid: false,
                    errorMessage: t(
                        'survey_form.category_page.error_invalid_data_format',
                    ),
                };
            }

            return { isValid: true };
        }

        // "ไม่แพ้" (No allergy) was selected on a ShortText question in the optional
        // category — treat as answered so the user can proceed without typing.
        if (
            currentQuestion.questionType === 'text' &&
            vals[0] === NO_ALLERGY_MARKER
        ) {
            return { isValid: true };
        }

        // For other question types, check if required
        if (!isRequired) {
            return { isValid: true };
        }

        // REQUIRED questions validation below:

        // Text questions must have non-empty value and meet length requirements
        if (currentQuestion.questionType === 'text') {
            const textValue = vals[0]?.trim() || '';

            if (!textValue) {
                return {
                    isValid: false,
                    errorMessage: t(
                        'survey_form.category_page.error_text_required',
                    ),
                };
            }

            const minLength = currentQuestion.questionConfig?.minLength || 0;
            if (minLength > 0 && textValue.length < minLength) {
                return {
                    isValid: false,
                    errorMessage: t(
                        'survey_form.category_page.error_text_too_short',
                        { minLength },
                    ),
                };
            }

            const maxLength = currentQuestion.questionConfig?.maxLength;
            if (maxLength && textValue.length > maxLength) {
                return {
                    isValid: false,
                    errorMessage: t(
                        'survey_form.category_page.error_text_too_long',
                        { maxLength },
                    ),
                };
            }

            return { isValid: true };
        }

        // Email questions must have valid email format
        if (currentQuestion.questionType === 'email') {
            const emailValue = vals[0]?.trim() || '';

            if (!emailValue) {
                return {
                    isValid: false,
                    errorMessage: t(
                        'survey_form.category_page.error_email_required',
                    ),
                };
            }

            const pattern =
                currentQuestion.questionConfig?.validationRules?.pattern;
            if (pattern) {
                try {
                    const regex = new RegExp(pattern);
                    if (!regex.test(emailValue)) {
                        return {
                            isValid: false,
                            errorMessage:
                                currentQuestion.questionConfig?.validationRules
                                    ?.message ||
                                t(
                                    'survey_form.category_page.error_email_invalid',
                                ),
                        };
                    }
                } catch (e) {
                    console.error('Invalid email regex pattern:', pattern);
                }
            }

            const basicEmailRegex =
                /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            if (!basicEmailRegex.test(emailValue)) {
                return {
                    isValid: false,
                    errorMessage: t(
                        'survey_form.category_page.error_email_invalid',
                    ),
                };
            }

            return { isValid: true };
        }

        // Checkbox questions - check minSelections
        if (currentQuestion.questionType === 'checkbox') {
            const minSelections =
                currentQuestion.questionConfig?.minSelections ?? 1;
            if (vals.length < minSelections) {
                if (minSelections === 1) {
                    return {
                        isValid: false,
                        errorMessage: t(
                            'survey_form.category_page.error_select_at_least_one',
                        ),
                    };
                }
                return {
                    isValid: false,
                    errorMessage: t(
                        'survey_form.category_page.error_select_minimum',
                        { minSelections },
                    ),
                };
            }
            const maxSelections = currentQuestion.questionConfig?.maxSelections;
            if (maxSelections && vals.length > maxSelections) {
                return {
                    isValid: false,
                    errorMessage: t(
                        'survey_form.category_page.error_select_maximum',
                        { maxSelections },
                    ),
                };
            }
            return { isValid: true };
        }

        // Multiselect must have at least one selection if required
        if (!vals || vals.length === 0) {
            return {
                isValid: false,
                errorMessage: t(
                    'survey_form.category_page.error_select_at_least_one',
                ),
            };
        }

        return { isValid: true };
    };

    const onNextQuestion = async () => {
        // Validate current question first
        const validation = validateCurrentQuestion();
        if (!validation.isValid) {
            console.warn('⚠️ Validation failed:', validation.errorMessage);
            // Show error to user
            setValidationErrorMessage(
                validation.errorMessage ||
                    t('survey_form.category_page.error_generic'),
            );
            // Clear error message after 5 seconds
            setTimeout(() => setValidationErrorMessage(null), 5000);
            return;
        }

        // Clear any previous error message when validation passes
        setValidationErrorMessage(null);

        // Emit a GTM event for every answered question (fires on each "next" after validation passes)
        const answeredCategory = ordered.find((c) => c.id === activeCategoryId);
        const answeredQuestion = answeredCategory?.questions?.[stepIndex];
        if (answeredQuestion) {
            const shownAt = questionShownAtRef.current;
            const timeOnQuestion = shownAt
                ? Math.max(0, Math.round((Date.now() - shownAt) / 1000))
                : 0;
            // True when the user went BACK to re-answer a question they had already
            // passed in this same in-progress attempt (the step was completed before
            // and they navigated back to it). Distinct from is_editing, which means
            // editing an already-completed/submitted survey.
            const isRevisit =
                (completedStepsPerCategory[activeCategoryId] || 0) > stepIndex;
            // An "edit" is re-answering a question on a survey that was already
            // completed (survey_complete fired → completionEventSentAt set) AND the
            // question had been answered before (is_revisit true).
            // In that case emit survey_edit_submit INSTEAD of survey_question_answered.
            const isEditContext =
                !!trackingRef.current.completionEventSentAt &&
                (isRevisit);
            const answeredQuestionPayload = {
                category_id: activeCategoryId,
                category_name: getText(
                    answeredCategory?.name,
                    answeredCategory?.nameEn,
                ),
                category_index: activeIndex + 1,
                question_number: stepIndex + 1,
                question_index: stepIndex,
                question_id:
                    answeredQuestion.questionId || answeredQuestion.id || '',
                question_text: getText(
                    answeredQuestion.question,
                    answeredQuestion.questionEn,
                ),
                question_type: answeredQuestion.type,
                time_on_question: timeOnQuestion, // seconds spent on this question
                is_editing: isEditingAnswers, // editing an already-completed survey
                is_revisit: isRevisit, // went back to re-answer a question mid-attempt
            };
            if (isEditContext) {
                emitSurveyEvent('survey_edit_submit', {
                    ...answeredQuestionPayload,
                    completion_state: 'edited',
                    edit_count: trackingRef.current.editCount || 0,
                });
            } else {
                emitSurveyEvent(
                    'survey_question_answered',
                    answeredQuestionPayload,
                );
            }
        }

        // Check if this is the last question (will trigger workflow execution)
        const isLastQuestion = stepIndex + 1 >= activeTotalSteps;

        // Prevent double-click during workflow processing (only for last question)
        if (isLastQuestion && isProcessingNext) {
            console.log(
                '⚠️ Already processing workflow, ignoring duplicate click',
            );
            return;
        }

        // Only set loading state for last question (workflow execution)
        if (isLastQuestion) {
            setIsProcessingNext(true);
        }

        try {
            // Save current answer to database before moving to next question
            const currentUserId =
                effectiveUserId || props.userId || createdGuestId;
            if (currentUserId && surveyForm?.id) {
                const category = ordered.find((c) => c.id === activeCategoryId);
                const question = category?.questions?.[stepIndex];
                const answerValues = currentAnswer?.values || [];

                // Transform answer data based on question type
                let transformedAnswerData: any = { values: answerValues };

                // SLIDER: Transform to structured format with units
                if (question?.type === 'slider' && answerValues[0]) {
                    try {
                        const sliderData = JSON.parse(answerValues[0]);
                        const fields =
                            question.config?.fields ||
                            question.config?.sliders ||
                            [];

                        const structuredSliders = fields.map((field: any) => {
                            const fieldKey = field.name || field.id;
                            const rawFieldValue = sliderData[fieldKey];
                            const isAnswered =
                                rawFieldValue &&
                                typeof rawFieldValue === 'object' &&
                                !Array.isArray(rawFieldValue)
                                    ? rawFieldValue.isAnswered === true
                                    : false;
                            const hasExplicitDefault = Array.isArray(
                                field.units,
                            )
                                ? field.units.some(
                                      (unit: any) => unit.isDefault === true,
                                  )
                                : false;
                            const normalizedUnits =
                                Array.isArray(field.units) &&
                                field.units.length > 0
                                    ? field.units.map(
                                          (unit: any, index: number) => ({
                                              value: unit.value,
                                              label: unit.label || unit.value,
                                              labelEn:
                                                  unit.labelEn ||
                                                  unit.label ||
                                                  unit.value,
                                              factor:
                                                  typeof unit.factor ===
                                                      'number' &&
                                                  unit.factor > 0
                                                      ? unit.factor
                                                      : 1,
                                              isDefault:
                                                  unit.isDefault === true ||
                                                  (!hasExplicitDefault &&
                                                      index === 0),
                                          }),
                                      )
                                    : field.unit
                                      ? [
                                            {
                                                value: field.unit,
                                                label: field.unit,
                                                labelEn: field.unit,
                                                factor: 1,
                                                isDefault: true,
                                            },
                                        ]
                                      : [];
                            const defaultUnit =
                                normalizedUnits.find(
                                    (unit: any) => unit.isDefault,
                                )?.value ||
                                normalizedUnits[0]?.value ||
                                field.unit ||
                                '';
                            const selectedUnit =
                                rawFieldValue &&
                                typeof rawFieldValue === 'object' &&
                                !Array.isArray(rawFieldValue)
                                    ? rawFieldValue.unit || defaultUnit
                                    : defaultUnit;
                            const rawValue =
                                rawFieldValue &&
                                typeof rawFieldValue === 'object' &&
                                !Array.isArray(rawFieldValue)
                                    ? rawFieldValue.value
                                    : rawFieldValue;
                            const selectedFactor =
                                normalizedUnits.find(
                                    (unit: any) => unit.value === selectedUnit,
                                )?.factor || 1;
                            const numericValue =
                                rawValue === undefined ||
                                rawValue === null ||
                                rawValue === '' ||
                                isNaN(Number(rawValue))
                                    ? field.min
                                    : Number(rawValue);
                            return {
                                name: fieldKey,
                                label: field.label,
                                labelEn: field.labelEn || '',
                                step: field.step,
                                min: field.min,
                                max: field.max,
                                value: numericValue,
                                unit: selectedUnit,
                                normalizedValue: numericValue / selectedFactor,
                                normalizedUnit: defaultUnit,
                                units: normalizedUnits,
                                isAnswered,
                            };
                        });

                        transformedAnswerData = { values: structuredSliders };
                    } catch (error) {
                        console.error(
                            '❌ Failed to transform slider data:',
                            error,
                        );
                    }
                }
                // MATRIX: Transform to structured format with actual values
                else if (question?.type === 'matrix' && answerValues[0]) {
                    try {
                        const matrixData = JSON.parse(answerValues[0]);
                        const rows = question.config?.rows || [];
                        const columns = question.config?.columns || [];

                        const structuredMatrix = rows
                            .map((row: any) => {
                                // matrixData uses row IDs as keys (e.g., {"row_1": "col_2"})
                                const rowId = row.id;
                                const columnId = matrixData[rowId];

                                // Find column by ID or value
                                const selectedColumn = columns.find(
                                    (col: any) =>
                                        col.id === columnId ||
                                        col.value === columnId,
                                );

                                // Extract actual value from column
                                // Use column.value as the actual value (this is what admin configured)
                                // This should match what's used in workflow conditions
                                let actualColumnValue =
                                    selectedColumn?.value || '';

                                return {
                                    rowId: rowId,
                                    rowLabel: row.label,
                                    rowValue: row.value || row.id || '',
                                    columnId: selectedColumn?.id || columnId,
                                    columnLabel: selectedColumn?.label || '',
                                    columnValue: actualColumnValue,
                                };
                            })
                            .filter((item: any) => item.columnId); // Only include answered rows

                        transformedAnswerData = { values: structuredMatrix };
                    } catch (error) {
                        console.error(
                            '❌ Failed to transform matrix data:',
                            error,
                        );
                    }
                }

                if (question) {
                    try {
                        const { saveSurveyResponse } = await import(
                            '@/actions/survey'
                        );
                        const saveResult = await saveSurveyResponse({
                            surveySlug: props.slug,
                            applicationId: surveyForm.id,
                            userId: currentUserId, // Use effectiveUserId or createdGuestId
                            categoryId: activeCategoryId,
                            questionId: question.questionId || question.id,
                            answerData: transformedAnswerData,
                            stepNumber: stepIndex,
                            isComplete: true, // Always true when "next" is clicked, regardless of answer
                            isRestarted: false,
                            restartGroupId: restartGroupId || undefined,
                        });
                        if (!saveResult.success) {
                            console.error(
                                '❌ Failed to save answer to database:',
                                saveResult.message,
                            );
                        }
                    } catch (error) {
                        console.error(
                            '❌ Failed to save answer to database:',
                            error,
                        );
                    }
                }
            }

            // Update completed steps for progress bar (count current step as completed when moving to next)
            const newCompletedCount = stepIndex + 1;
            const nextCompletedSteps = {
                ...completedStepsPerCategory,
                [activeCategoryId]: Math.max(
                    completedStepsPerCategory[activeCategoryId] || 0,
                    newCompletedCount,
                ),
            };
            setCompletedStepsPerCategory(nextCompletedSteps);
            if (
                !isEditingAnswers &&
                areRequiredQuestionsCompleted(nextCompletedSteps)
            ) {
                // Awaited so the counter write lands before
                // runPreservingCompletionCounts below reads it — a fire-and-forget
                // increment still in flight would be read as its pre-bump value and
                // then restored over, losing the count.
                await emitSurveyComplete(nextCompletedSteps);
            }
            // Fires once when the user FIRST answers every question (optional
            // included) — including during an edit session: a survey marked
            // completed with optional questions still unanswered reaches its
            // first genuine full completion while isEditingAnswers is true.
            // Captured BEFORE emitting so the safety net below (reached after
            // several awaits, by which point trackingRef may have caught up
            // to a fullCompletedAt just set here) routes consistently instead
            // of re-deriving from state that could have changed in between.
            const wasAlreadyFullyCompleted = !!trackingRef.current
                .fullCompletedAt;
            // Gate on isOnFinalCategoryForCompletion too: stepIndex + 1 >=
            // activeTotalSteps is true at the end of EVERY category, not
            // just the survey's final one. Without this, an edit session on
            // an already-fully-answered survey would fire the moment the
            // user clicks "next" past the FIRST category they happen to
            // revisit — the all-required/all-optional guards inside the emit
            // functions are already satisfied from before this click even
            // started, so any category boundary would pass them. Captured
            // under its own name (not the block-scoped `isLastCategory`
            // declared further below for the survey-level-workflow trigger)
            // to avoid a TDZ collision with that later declaration.
            const isOnFinalCategoryForCompletion =
                activeIndex === ordered.length - 1;
            if (
                isOnFinalCategoryForCompletion &&
                stepIndex + 1 >= activeTotalSteps
            ) {
                if (wasAlreadyFullyCompleted) {
                    // Already fully completed in a PAST session — this edit
                    // round finishing now re-fires as 'edited', not 'full'.
                    await emitSurveyFullCompleteAfterEdit(nextCompletedSteps);
                } else {
                    // Awaited, and sequenced after emitSurveyComplete above: on a
                    // survey with no optional questions both fire on this same
                    // click, and two concurrent read-modify-write counter updates
                    // would lose one of the two increments.
                    await emitSurveyFullComplete(nextCompletedSteps);
                }
            }

            if (stepIndex + 1 < activeTotalSteps) {
                // push current question state before moving forward
                pushHistory({
                    categoryId: activeCategoryId,
                    mode: 'question',
                    stepIndex,
                });
                setStepIndex(stepIndex + 1);
            } else {
                // moving from last question to result
                pushHistory({
                    categoryId: activeCategoryId,
                    mode: 'question',
                    stepIndex,
                });

                // Execute workflow and fetch result from database
                const workflowUserId =
                    effectiveUserId || props.userId || createdGuestId;
                if (workflowUserId && surveyForm?.id) {
                    setLoadingWorkflowResult(true);

                    try {
                        // Step 1: Execute workflow (trigger Backend calculation)
                        console.log(
                            '🔄 Executing workflow for category:',
                            activeCategoryId,
                        );
                        const executionResult = await CallAPI_ExecuteWorkflow({
                            applicationId: surveyForm.id,
                            categoryId: activeCategoryId,
                            userId: workflowUserId, // Use effectiveUserId
                        });

                        console.log('📦 Execution result received:', {
                            success: executionResult.success,
                            statusCode: executionResult.statusCode,
                            totalExecuted:
                                executionResult.result?.totalExecuted,
                            executionsCount:
                                executionResult.result?.executions?.length,
                            errors: executionResult.error,
                        });

                        if (executionResult.success) {
                            console.log(
                                '✅ Workflow executed successfully:',
                                executionResult.result,
                            );

                            // Safety net: survey_full_complete should already
                            // have fired above when the last question was
                            // answered. If it has not been sent yet for this
                            // attempt, retry here so a fully answered survey
                            // never completes its workflow without the event.
                            // Routes on the SAME wasAlreadyFullyCompleted
                            // captured above (not on fullCompleteSentRef,
                            // which only means "something was emitted this
                            // round" and would misroute an edit-round
                            // completion to the wrong variant). Each target
                            // function has its own dedup ref, so calling it
                            // again here is always a safe no-op if the
                            // immediate emit above already fired. Gated on
                            // isOnFinalCategoryForCompletion for the same
                            // reason as above — this workflow runs at the
                            // end of EVERY category, not just the survey's
                            // final one.
                            if (isOnFinalCategoryForCompletion) {
                                if (wasAlreadyFullyCompleted) {
                                    await emitSurveyFullCompleteAfterEdit(
                                        nextCompletedSteps,
                                    );
                                } else {
                                    await emitSurveyFullComplete(
                                        nextCompletedSteps,
                                    );
                                }
                            }

                            // Step 2: Fetch the result with Server Action
                            const { fetchWorkflowResult } = await import(
                                '@/actions/workflow'
                            );
                            const result = await fetchWorkflowResult(
                                workflowUserId,
                                surveyForm.id,
                                activeCategoryId,
                            );

                            if (result) {
                                setWorkflowResult(result);
                                console.log(
                                    '✅ Workflow result loaded:',
                                    result,
                                );
                            } else {
                                console.error('❌ No workflow result found');
                            }

                            // Step 3: Save category data to user profile after workflow completes
                            // Fetch transformed data from SfSurveyResponse and save to metadata
                            // saveCategoryDataToProfile replaces the profile
                            // metadata wholesale — preserve the lifetime
                            // completion counters across it.
                            await runPreservingCompletionCounts(
                                getCurrentUserId() || '',
                                () =>
                                    saveCategoryDataToProfile(activeCategoryId),
                            );

                            // Survey-level workflows (Recommendation / Persona) are
                            // intentionally NOT executed here. setMode('result')
                            // below triggers finalSurveyHandle() → complete-all-
                            // workflows, which runs them AFTER every category's
                            // colors are recalculated. Executing them inline at
                            // this point ran them against stale category results
                            // and duplicated their execution-log rows (DF-016).
                        } else {
                            console.error(
                                '❌ Workflow execution failed:',
                                executionResult.error_friendly_message?.th ||
                                    executionResult.error?.[0] ||
                                    'Unknown error',
                            );
                            console.error(
                                'Full error details:',
                                executionResult,
                            );
                        }
                    } catch (error) {
                        console.error('❌ Workflow API error:', error);
                        console.error(
                            'Error stack:',
                            error instanceof Error
                                ? error.stack
                                : 'No stack trace',
                        );
                    } finally {
                        setLoadingWorkflowResult(false);
                    }
                }

                setMode('result');
            }
        } catch (error) {
            console.error('❌ Error in onNextQuestion:', error);
            // Still allow navigation even if there's an error
            if (stepIndex + 1 < activeTotalSteps) {
                setStepIndex(stepIndex + 1);
            } else {
                setMode('result');
            }
        } finally {
            // Only reset loading state if it was set (for last question)
            if (stepIndex + 1 >= activeTotalSteps) {
                setIsProcessingNext(false);
            }
        }
    };

    // Back button: go to previous question or category intro if at first question
    const onBackQuestion = () => {
        if (mode !== 'question') return;
        emitSurveyEvent('survey_navigate_back', {
            back_type: 'question',
            ...getSurveyPosition(),
        });
        if (stepIndex > 0) {
            setStepIndex(stepIndex - 1);
        } else {
            // back from first question returns to intro
            setMode('categoryIntro');
        }
    };

    // Close button: always return to category intro (does not reset answers)
    const onCloseSurvey = () => {
        setMode('categoryIntro');
        setStepIndex(0);
    };

    const onNextFromResult = async () => {
        if (activeIndex + 1 < ordered.length) {
            // push current result state before navigating to next category intro
            pushHistory({
                categoryId: activeCategoryId,
                mode: 'result',
                stepIndex: activeTotalSteps - 1,
            });
            goToCategory(activeIndex + 1);
        }
        // Finishing the last category deliberately keeps the saved progress.
        // `isCompleted` already marks the survey as done, and both the resume
        // dialog and the edit session read the SAME record to put the answers
        // back — clearing it here left "แก้ไขแบบฟอร์ม" with nothing to load.
        // Only "เริ่มใหม่" deletes: see handleRestart in category-page-wrapper
        // and the start-fresh branch in resume-decision-page.
    };

    const categoryResultAnswers = answers[activeCategoryId] || [];
    const isLastStep =
        activeIndex === ordered.length - 1 &&
        stepIndex === activeTotalSteps - 1;
    const isFinalInsightsLoading =
        isFinalCategoryResult &&
        (!hasFetchedCompleteSurveyResults ||
            !hasFetchedPersonaHtml ||
            !hasFetchedPersonaData ||
            !hasFetchedNutrientData);
    const isLastCategory = activeIndex === ordered.length - 1;
    const isLastCategoryResult = isLastCategory && mode === 'result';
    const showShareButtons = isLastStep || isLastCategoryResult; // Show share on last step OR last category results
    const isCategoryIntro = mode === 'categoryIntro';
    const isOptionalCategoryIntro =
        mode === 'categoryIntro' && activeCategory?.isOptional === true;
    const isExplicitEditMode = searchParams.get('action') === 'edit';
    const isResumeDecisionEditMode =
        props.isCompletedSurvey === true && !props.showResumeContent;
    const isEditingAnswers =
        isEditFlow || isExplicitEditMode || isResumeDecisionEditMode;
    /**
     * DF #111: In resume ("ทำต่อ") mode on an INCOMPLETE survey, all three
     * disjuncts of isEditingAnswers are correctly false — a resume is not an
     * edit session, and handleContinueFromResume explicitly calls
     * setIsEditFlow(false) for that path. But the optional (option-type)
     * category's card was gated on isEditingAnswers alone, so it only rendered
     * when it was already the active category. A user resuming on any other
     * category therefore had no way to reach it: no card, not clickable, and an
     * already-answered optional category routed straight to the results instead
     * of opening for edit — the reported "only the summary button shows".
     *
     * This is a NAVIGATION concern, not an edit-session concern, so it gets its
     * own predicate. Reusing isEditingAnswers would wrongly emit
     * survey_start{entry_source:'edit'} and change 7 other gates.
     */
    const canRevisitAnsweredCategories = isEditingAnswers || hasResumedSession;
    // DF #111: whether every required category is answered. Skipping to the final
    // result persists `isCompleted: true`, so the skip affordance must not appear
    // while required questions remain.
    const hasAnsweredAllRequiredCategories = areRequiredQuestionsCompleted(
        completedStepsPerCategory,
    );
    // Emit a GTM event when the user closes/exits the survey, capturing where they left off
    const emitSurveyClose = () => {
        const currentCategory = ordered.find((c) => c.id === activeCategoryId);
        const currentQuestion =
            mode === 'question'
                ? currentCategory?.questions?.[stepIndex]
                : undefined;
        emitSurveyEvent('survey_close', {
            closed_at: new Date().toISOString(),
            close_mode: mode, // จุดที่กดปิด: categoryIntro / question / result
            category_id: activeCategoryId,
            category_name: getText(
                currentCategory?.name,
                currentCategory?.nameEn,
            ),
            category_index: activeIndex + 1,
            question_number: mode === 'question' ? stepIndex + 1 : undefined,
            question_id: currentQuestion
                ? currentQuestion.questionId || currentQuestion.id || ''
                : undefined,
            question_text: currentQuestion
                ? getText(currentQuestion.question, currentQuestion.questionEn)
                : undefined,
            is_editing: isEditingAnswers,
        });
    };
    const activeCategoryQuestionCount =
        activeCategory?.questions?.length || activeTotalSteps;
    const activeCategoryCompletedSteps =
        completedStepsPerCategory[activeCategoryId] || 0;
    const isActiveCategoryAnswered =
        activeCategoryQuestionCount > 0 &&
        activeCategoryCompletedSteps >= activeCategoryQuestionCount;
    const isAnsweredOptionalCategoryIntro =
        isOptionalCategoryIntro && isActiveCategoryAnswered;
    // DF #111: an already-answered optional category must open for EDIT rather
    // than jumping to the results, in a resumed session too.
    const shouldEditAnsweredOptionalCategory =
        isAnsweredOptionalCategoryIntro && canRevisitAnsweredCategories;
    /**
     * DF #111: also offer "ข้ามไปดูผลลัพธ์ของคุณ" in a resumed session.
     *
     * Edit mode requires hasStartedEditCategory (set by onStartCategory), which
     * means the button only appears from the SECOND intro onward. A resuming user
     * gets it on the first intro instead — that intro is the screen they land on,
     * and requiring them to open a category first defeats the purpose.
     *
     * Gated on hasAnsweredAllRequired: skipping leads to the final result page,
     * which persists `isCompleted: true`, so it must not be reachable while
     * required categories are still unanswered.
     *
     * `!isOptionalCategoryIntro` keeps this mutually exclusive with the optional-
     * category footer below — both blocks render `#footer-button-container` with
     * the same `footerRef`, so overlapping them produced two stacked footers and
     * a mis-measured spacer.
     */
    const shouldShowEditIntroSkipButton =
        isCategoryIntro &&
        !isOptionalCategoryIntro &&
        (isEditingAnswers
            ? hasStartedEditCategory
            : hasResumedSession && hasAnsweredAllRequiredCategories);
    /**
     * DF #111: the optional category has its OWN footer block, whose skip button
     * is gated on `!isAnsweredOptionalCategoryIntro || <this>`. Once the optional
     * category is already answered the first half is false, so this is the only
     * thing that can show the button there.
     *
     * It must NOT reuse shouldShowEditIntroSkipButton: that one is deliberately
     * false on the optional intro (the two footer blocks would otherwise both
     * render `#footer-button-container` and stack). Hence a sibling predicate.
     *
     * No completion gate here: this button is inside the optional-category footer,
     * and skipping an OPTIONAL category is exactly what it is for. The required
     * ones are unaffected — onSkipOptionalCategory only jumps to the final result
     * when every required category is answered, otherwise it shows this
     * category's own result and the flow continues normally.
     */
    const shouldShowOptionalSkipButtonForRevisit =
        isOptionalCategoryIntro && canRevisitAnsweredCategories;
    const shouldShowEditAnswerCta = isEditingAnswers;
    /**
     * DF #111: In a resumed ("ทำต่อ") session the user is continuing an
     * unfinished survey, not editing a finished one — so both "แก้ไขคำตอบ" and
     * "เริ่มทำแบบสอบถาม" read wrong. Use "ทำแบบสอบถามต่อ" instead.
     *
     * Scoped to a genuine resume: isEditingAnswers takes priority, so returning
     * to a COMPLETED survey still says "แก้ไขคำตอบ".
     */
    const isContinuingSurvey = !isEditingAnswers && hasResumedSession;
    const continueSurveyText = t(
        'survey_form.category_page.continue_survey',
        'ทำแบบสอบถามต่อ',
    );
    const categoryIntroPrimaryButtonText = shouldShowEditAnswerCta
        ? t('survey_form.category_page.edit_answers', 'แก้ไขคำตอบ')
        : isContinuingSurvey
          ? continueSurveyText
          : t('survey.start');
    const optionalPrimaryButtonText = isAnsweredOptionalCategoryIntro
        ? shouldEditAnsweredOptionalCategory
            ? isContinuingSurvey
                ? continueSurveyText
                : t('survey_form.category_page.edit_answers', 'แก้ไขคำตอบ')
            : t(
                  'survey_form.category_page.view_your_results',
                  'ดูผลลัพธ์ของคุณ',
              )
        : isContinuingSurvey
          ? continueSurveyText
          : t('survey_form.category_page.interested_start', 'สนใจเริ่มเลย');
    const isInitializing = props.isInitializing === true;
    // Also true while initializing, so every footer action stays hidden until progress is known.
    const hasResumeOverlay = props.showResumeContent === true || isInitializing;
    const showFixedFooter =
        !hasResumeOverlay &&
        (mode === 'question' ||
            mode === 'result' ||
            isOptionalCategoryIntro ||
            shouldShowEditIntroSkipButton); // Show footer for question/result and intro states that need sticky actions
    const shouldShowInlineCategoryIntroButton =
        activeTotalSteps > 0 &&
        !activeCategory?.isOptional &&
        !shouldShowEditIntroSkipButton;
    // DF #111: use the navigation predicate, so a resumed session can still see
    // and open the optional (option-type) category.
    const shouldRenderOptionalCard =
        !!optionalCategory &&
        (activeCategoryId === optionalCategory.id ||
            canRevisitAnsweredCategories);
    const navigateToSpecificResult = async () => {
        setIsNavigatingToResult(true);

        if (isEditingAnswers && trackingRef.current.editSessionStartedAt) {
            // Per-question survey_edit_submit already fired while editing. Here we
            // only re-fire survey_full_complete (completion_state: 'edited') to
            // summarise the finished edit round. The edit session is reset on the
            // next entry into edit mode (see handleContinueFromResume), not here —
            // clearing it while still in edit mode would immediately re-trigger the
            // edit-session effect and emit a spurious survey_start.
            // Awaited: router.push below tears this view down, so the event has to
            // be on the dataLayer before we navigate.
            await emitSurveyFullCompleteAfterEdit();
        }

        const currentUserId = getCurrentUserId();
        if (currentUserId) {
            await markSurveyAsCompleted(props.slug, currentUserId);
        }

        const resultPath = props.lang
            ? `/${props.lang}/survey-form/${props.slug}/specific-result`
            : `/survey-form/${props.slug}/specific-result`;
        router.push(resultPath);
    };

    // Entering an edit session after survey_complete counts as a new visit:
    // bump both visit_count and edit_count and emit survey_start with
    // entry_source 'edit'.
    useEffect(() => {
        if (!isEditingAnswers) {
            return;
        }

        if (
            !tracking.completionEventSentAt ||
            tracking.editSessionStartedAt ||
            !restartGroupId
        ) {
            return;
        }

        const nowIso = new Date().toISOString();
        const nextTracking = markSurveyEditStartTracking(
            { tracking: startSurveyTrackingSession({ tracking }, nowIso) },
            nowIso,
        );
        setTracking(nextTracking);
        emitSurveyEvent('survey_start', {
            entry_source: 'edit',
            first_started_at: nextTracking.firstStartedAt,
            started_at: nowIso,
            visit_count: nextTracking.visitCount,
            edit_count: nextTracking.editCount || 0,
            active_duration_seconds: nextTracking.activeDurationSeconds || 0,
        });
    }, [isEditingAnswers, restartGroupId, tracking]);

    // If a category has no workflow result to display, skip its result page and continue.
    useEffect(() => {
        if (hasResumeOverlay) return;
        if (mode !== 'result') return;
        if (loadingWorkflowResult) return;
        if (workflowResult) return;
        if (showShareButtons) return;
        if (activeIndex + 1 >= ordered.length) return;

        const timer = window.setTimeout(() => {
            goToCategory(activeIndex + 1);
        }, 0);

        return () => window.clearTimeout(timer);
    }, [
        hasResumeOverlay,
        mode,
        loadingWorkflowResult,
        workflowResult,
        showShareButtons,
        activeIndex,
        ordered.length,
    ]);

    useEffect(() => {
        if (mode !== 'result' && loadingWorkflowResult) {
            setLoadingWorkflowResult(false);
        }
    }, [mode, loadingWorkflowResult]);

    useEffect(() => {
        const fetchShareUrl = async () => {
            const currentUserId =
                effectiveUserId || props.userId || createdGuestId;

            if (!showShareButtons || !currentUserId) {
                return;
            }

            setIsLoadingShareUrl(true);

            try {
                const url = await getShareUrlForUser(currentUserId, 'th');

                if (url) {
                    setShareUrl(url);
                } else {
                    shareUrlFetchedRef.current = false;
                }
            } catch (error) {
                console.error('❌ Failed to generate share URL:', error);
                shareUrlFetchedRef.current = false;
            } finally {
                setIsLoadingShareUrl(false);
            }
        };

        fetchShareUrl();
    }, [
        showShareButtons,
        effectiveUserId,
        props.userId,
        createdGuestId,
        latestRecExecutedAt,
    ]);

    useEffect(() => {
        if (!showShareButtons || !shareUrl) return;

        const executionResultId = shareUrl.split('/').pop();
        if (!executionResultId) return;

        const preloadImage = async () => {
            try {
                const baseUrl = window.location.origin;
                const ogImageUrl = `${baseUrl}/api/og/${executionResultId}`;

                console.log(
                    '🔄 Preloading OG image for sharing:',
                    executionResultId,
                );
                const response = await fetch(ogImageUrl);
                if (response.ok) {
                    const blob = await response.blob();
                    setPreloadedImageBlob(blob);
                    console.log(
                        '✅ OG image preloaded, ready for instant sharing',
                    );
                }
            } catch (err) {
                console.error('Failed to preload image:', err);
            }
        };

        preloadImage();
    }, [shareUrl, showShareButtons]);

    // Dynamic scale for categoryIntro so entire block fits between header and footer without scroll
    const introWrapperRef = useRef<HTMLDivElement | null>(null);
    const [introScale, setIntroScale] = useState(1);
    const [introReady, setIntroReady] = useState(false);
    const introMeasuredRef = useRef(false);

    // Dynamic header height spacer logic
    const headerRef = useRef<HTMLDivElement | null>(null);
    const [headerHeight, setHeaderHeight] = useState<number>(75);

    useEffect(() => {
        const el = headerRef.current;
        if (!el) return;
        const update = () => {
            setHeaderHeight(el.getBoundingClientRect().height);
        };
        update();
        const g: any = globalThis as any;
        if (g.ResizeObserver) {
            const ro = new g.ResizeObserver(() => update());
            ro.observe(el);
            return () => ro.disconnect();
        } else {
            g.addEventListener?.('resize', update);
            return () => g.removeEventListener?.('resize', update);
        }
    }, []);

    // Dynamic footer height. The footer is position:fixed at the bottom, so the
    // scale calculations below MUST subtract its height or the last question
    // option / answer ends up hidden behind it. We measure the real rendered
    // footer instead of hard-coding, since it differs by breakpoint (~76–88px)
    // and grows when the button/label wraps.
    const footerRef = useRef<HTMLDivElement | null>(null);
    const [footerHeight, setFooterHeight] = useState<number>(88);

    useEffect(() => {
        const el = footerRef.current;
        // In modes without a footer (result/intro), fall back to 0 so the scale
        // math uses the full available height.
        if (!el) {
            setFooterHeight(0);
            return;
        }
        const update = () => {
            setFooterHeight(el.getBoundingClientRect().height);
        };
        update();
        const g: any = globalThis as any;
        if (g.ResizeObserver) {
            const ro = new g.ResizeObserver(() => update());
            ro.observe(el);
            return () => ro.disconnect();
        } else {
            g.addEventListener?.('resize', update);
            return () => g.removeEventListener?.('resize', update);
        }
        // Re-run whenever a footer mounts/unmounts (mode change or the intro
        // footer-visibility flags toggling) so the ref is (re)measured.
    }, [
        mode,
        hasResumeOverlay,
        showFixedFooter,
        isOptionalCategoryIntro,
        shouldShowEditIntroSkipButton,
    ]);

    // Real visible viewport height. On real phones the browser's dynamic toolbar
    // (address bar) retracts/expands, so `window.innerHeight` and `100vh` report
    // the LARGEST height (toolbar hidden) even while the toolbar is still on
    // screen at load — the extra pixels sit behind the toolbar and content there
    // is not actually visible. DevTools mobile emulation has no dynamic toolbar,
    // so it never reproduces this. `visualViewport.height` reflects what the user
    // can truly see; we track it and use it for every fit/scale calculation.
    const [viewportHeight, setViewportHeight] = useState<number>(() =>
        typeof window !== 'undefined'
            ? window.visualViewport?.height ?? window.innerHeight
            : 0,
    );

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const vv = window.visualViewport;
        const update = () => {
            setViewportHeight(vv?.height ?? window.innerHeight);
        };
        update();
        if (vv) {
            vv.addEventListener('resize', update);
            vv.addEventListener('scroll', update);
            return () => {
                vv.removeEventListener('resize', update);
                vv.removeEventListener('scroll', update);
            };
        }
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);

    // Dynamic scale for categoryIntro so the whole block fits between the fixed
    // header and footer without scrolling. Declared AFTER headerHeight /
    // footerHeight / viewportHeight so it can depend on them.
    useEffect(() => {
        if (mode !== 'categoryIntro') {
            // Reset when leaving intro mode
            introMeasuredRef.current = false;
            setIntroReady(false);
            setIntroScale(1);
            return;
        }

        let rafId: number;
        let fallbackTimeoutId: ReturnType<typeof setTimeout> | undefined;
        const computeScale = () => {
            const el = introWrapperRef.current;
            if (!el) {
                rafId = requestAnimationFrame(computeScale);
                return;
            }

            // Real visible height (mobile-toolbar aware) minus the fixed header
            // and, when present, the fixed footer — otherwise the intro block is
            // scaled to fit the whole screen and its bottom is hidden behind the
            // footer button on real phones. 32px is breathing room.
            const viewportH =
                window.visualViewport?.height ?? window.innerHeight;
            const availableHeight =
                viewportH - headerHeight - footerHeight - 32;

            // Get scroll height for natural content size (doesn't require style reset)
            const contentHeight = el.scrollHeight || el.offsetHeight;

            if (contentHeight <= 0) {
                rafId = requestAnimationFrame(computeScale);
                return;
            }

            let newScale = 1;
            if (contentHeight > availableHeight) {
                newScale = Math.max(
                    0.6,
                    Math.min(1, availableHeight / contentHeight),
                );
            }

            setIntroScale(newScale);

            // Mark as ready after first measurement
            if (!introMeasuredRef.current) {
                introMeasuredRef.current = true;
                // Small delay to ensure scale is applied before showing
                rafId = requestAnimationFrame(() => {
                    setIntroReady(true);
                });
            }
        };

        // Initial computation after a frame to ensure DOM is ready
        rafId = requestAnimationFrame(computeScale);
        fallbackTimeoutId = setTimeout(() => {
            setIntroReady(true);
        }, 150);

        window.addEventListener('resize', computeScale);
        const vv = window.visualViewport;
        vv?.addEventListener('resize', computeScale);
        vv?.addEventListener('scroll', computeScale);
        return () => {
            window.removeEventListener('resize', computeScale);
            vv?.removeEventListener('resize', computeScale);
            vv?.removeEventListener('scroll', computeScale);
            if (rafId) cancelAnimationFrame(rafId);
            if (fallbackTimeoutId) clearTimeout(fallbackTimeoutId);
        };
    }, [
        mode,
        activeCategoryId,
        ordered.length,
        headerHeight,
        footerHeight,
        viewportHeight,
    ]);

    // Global page scaling - simplified, only used for question mode
    // categoryIntro uses its own introScale, result mode has no scaling
    const containerRef = useRef<HTMLDivElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const [pageScale, setPageScale] = useState(1);

    useEffect(() => {
        // Only compute pageScale for question mode
        if (mode !== 'question') {
            setPageScale(1);
            return;
        }

        let rafId: number;
        const recomputeScale = () => {
            const content = contentRef.current;
            if (!content) return;

            // Real visible height (accounts for the mobile browser toolbar), minus
            // the fixed header AND the fixed footer, so scaled content never ends
            // up hidden behind the "Next" footer. The extra 8px is breathing room.
            const viewportH =
                window.visualViewport?.height ?? window.innerHeight;
            const availableH = viewportH - headerHeight - footerHeight - 8;

            // Use scrollHeight to get natural size without modifying styles
            const naturalH = content.scrollHeight || content.offsetHeight;

            let scale = 1;
            if (naturalH > availableH && naturalH > 0) {
                scale = Math.max(0.55, Math.min(1, availableH / naturalH));
            }

            setPageScale(scale);
        };

        rafId = requestAnimationFrame(recomputeScale);
        window.addEventListener('resize', recomputeScale);
        const vv = window.visualViewport;
        vv?.addEventListener('resize', recomputeScale);
        vv?.addEventListener('scroll', recomputeScale);
        return () => {
            window.removeEventListener('resize', recomputeScale);
            vv?.removeEventListener('resize', recomputeScale);
            vv?.removeEventListener('scroll', recomputeScale);
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [mode, stepIndex, headerHeight, footerHeight, viewportHeight]);

    /**
     * Arriving with ?action=edit skips the resume dialog, and that dialog's
     * "continue" handler is the ONLY place that puts saved answers back into
     * state — so an edit session opened blank and looked like a survey that had
     * never been answered.
     *
     * Restores the DATA only. Navigation stays with the URL the user arrived on,
     * and the resume-specific analytics stay on the dialog path, so this cannot
     * report an edit as a resumed session.
     */
    useEffect(() => {
        if (searchParams.get('action') !== 'edit') {
            return;
        }
        if (editAnswersRestoredRef.current) {
            return;
        }

        const currentUserId = effectiveUserId || props.userId || createdGuestId;
        if (!currentUserId) {
            // A guest id can arrive a tick later; the effect re-runs with it.
            return;
        }
        editAnswersRestoredRef.current = true;

        let cancelled = false;

        void (async () => {
            let savedProgress = null as Awaited<
                ReturnType<typeof loadSurveyProgress>
            >;
            try {
                const ttlMs = await getTTL('NEXT_PUBLIC_SURVEY_TTL_SECONDS');
                savedProgress = await loadSurveyProgress(
                    props.slug,
                    currentUserId,
                    { ttlMs },
                );
            } catch {
                savedProgress = await loadSurveyProgress(
                    props.slug,
                    currentUserId,
                );
            }

            if (cancelled || !savedProgress) {
                return;
            }

            setTracking(getSurveyTrackingState(savedProgress.tracking));
            setAnswers(savedProgress.answers || {});
            setCompletedStepsPerCategory(
                savedProgress.completedStepsPerCategory || {},
            );
            setVisitedCategories(new Set(savedProgress.visitedCategories || []));
        })();

        return () => {
            cancelled = true;
        };
    }, [
        searchParams,
        effectiveUserId,
        props.userId,
        createdGuestId,
        props.slug,
    ]);

    // Handlers for resume decision
    const handleContinueFromResume = async () => {
        // Get effective userId (props.userId or createdGuestId)
        const currentUserId = effectiveUserId || props.userId || createdGuestId;

        if (!currentUserId) {
            console.error('❌ Cannot load progress: No userId available');
            return;
        }

        let savedProgress = null as Awaited<
            ReturnType<typeof loadSurveyProgress>
        >;
        try {
            const ttlMs = await getTTL('NEXT_PUBLIC_SURVEY_TTL_SECONDS');
            savedProgress = await loadSurveyProgress(
                props.slug,
                currentUserId,
                { ttlMs },
            );
        } catch {
            savedProgress = await loadSurveyProgress(props.slug, currentUserId);
        }
        if (savedProgress) {
            setTracking(getSurveyTrackingState(savedProgress.tracking));
            // Check if user has reached the final result page (completed all categories)
            // This happens when: isCompleted flag is true, OR user is on last category result page
            const hasReachedFinalResult =
                savedProgress.isCompleted ||
                (savedProgress.currentMode === 'result' &&
                    savedProgress.currentCategoryId ===
                        ordered[ordered.length - 1]?.id);

            // If user has reached final result (editing mode), reset state and go to last category
            if (hasReachedFinalResult) {
                console.log(
                    '🔄 User has completed all categories - entering edit mode, going to last category',
                );
                hasCompletedAttemptRef.current = true;
                setIsEditFlow(true);
                // DF #111: this is the EDIT branch, not a resume — clear the flag
                // so the resume-specific gates don't stay armed alongside
                // isEditFlow (which already covers everything they enable).
                setHasResumedSession(false);

                // Start a fresh edit session: clear any editSessionStartedAt left over
                // from a previous edit round so the edit-session effect fires again
                // (bumping edit_count and emitting survey_start) for this new
                // entry into edit mode.
                setTracking((prev) => ({
                    ...prev,
                    editSessionStartedAt: undefined,
                }));

                // Restore answers and visited categories, but reset to last category intro
                setAnswers(savedProgress.answers || {});
                setCompletedStepsPerCategory(
                    savedProgress.completedStepsPerCategory || {},
                );
                setVisitedCategories(
                    new Set(savedProgress.visitedCategories || []),
                );
                setHasStartedEditCategory(false);

                // Go to last category intro (not question mode, not result mode)
                setMode('categoryIntro');
                setStepIndex(0);

                // Navigate to last category
                const lastCategory = ordered[ordered.length - 1];
                if (lastCategory && lastCategory.id !== activeCategoryId) {
                    const params = new URLSearchParams(searchParams.toString());
                    params.set('category', lastCategory.id);
                    router.replace(`?${params.toString()}`, { scroll: false });
                }

                // Clear history stack
                setHistory([]);
            } else {
                setIsEditFlow(false);
                // DF #111: not an edit session, but answered categories (notably
                // the optional option-type one) must stay reachable from here.
                setHasResumedSession(true);
                const resumedAt = new Date().toISOString();
                const resumedTracking = startSurveyTrackingSession(
                    { tracking: savedProgress.tracking },
                    resumedAt,
                );
                // Continuing a session after survey_complete counts as an edit
                // round: bump edit_count and mark the edit session as started
                // so the edit-session effect doesn't bump it a second time.
                const nextTracking = resumedTracking.completionEventSentAt
                    ? markSurveyEditStartTracking(
                          { tracking: resumedTracking },
                          resumedAt,
                      )
                    : resumedTracking;
                setTracking(nextTracking);
                emitSurveyEvent('survey_start', {
                    entry_source: 'resume',
                    first_started_at: nextTracking.firstStartedAt,
                    started_at: resumedAt,
                    visit_count: nextTracking.visitCount,
                    edit_count: nextTracking.editCount || 0,
                    active_duration_seconds:
                        nextTracking.activeDurationSeconds || 0,
                });

                // In-progress survey - restore exact position
                // First, update URL to correct category (this will trigger re-render with new activeCategoryId)
                // If user arrived with an explicit ?category= URL param (e.g., from Profile "สนใจเริ่มเลย"),
                // use it instead of the saved currentCategoryId so the intended category is honoured.
                const categoryToRestore =
                    initialCategoryParam.current &&
                    initialCategoryParam.current !==
                        savedProgress.currentCategoryId
                        ? initialCategoryParam.current
                        : savedProgress.currentCategoryId;

                if (
                    categoryToRestore &&
                    categoryToRestore !== activeCategoryId
                ) {
                    const params = new URLSearchParams(searchParams.toString());
                    params.set('category', categoryToRestore);
                    router.replace(`?${params.toString()}`, { scroll: false });

                    // Wait a tiny bit for URL to update and component to re-render with correct activeCategoryId
                    await new Promise((resolve) => setTimeout(resolve, 50));
                }

                // Then restore all states
                setAnswers(savedProgress.answers || {});
                setCompletedStepsPerCategory(
                    savedProgress.completedStepsPerCategory || {},
                );
                setVisitedCategories(
                    new Set(savedProgress.visitedCategories || []),
                ); // Restore visited categories

                // If we redirected to a different category than the one saved (e.g., Profile → optional),
                // land on that category's intro page instead of the saved mid-category position.
                const isOverridingCategory =
                    categoryToRestore !== savedProgress.currentCategoryId;
                setMode(
                    isOverridingCategory
                        ? 'categoryIntro'
                        : savedProgress.currentMode || 'categoryIntro',
                );
                setStepIndex(
                    isOverridingCategory
                        ? 0
                        : savedProgress.currentStepIndex || 0,
                );
                setHasStartedEditCategory(false);

                // Clear history stack because old history doesn't match restored state
                // This prevents incorrect back navigation
                setHistory([]);
            }
        }

        // Hide resume content
        if (props.onResumeDecisionMade) {
            props.onResumeDecisionMade();
        }
    };

    const handleRestartFromResume = async () => {
        hasCompletedAttemptRef.current = false;
        setIsEditFlow(false);
        // DF #111: a restart is a brand-new attempt, not a resumed session — clear
        // the flag so the skip-to-result button and the revisit gates do not carry
        // over into a survey the user is starting from scratch.
        setHasResumedSession(false);
        setTracking(getSurveyTrackingState());
        fullCompleteSentRef.current = false; // allow survey_full_complete to fire again for the new attempt
        editCompleteSentRef.current = false;
        completeSentRef.current = false; // ...and survey_complete, for the new attempt
        // completionCountsRef is deliberately NOT reset: the counts are lifetime
        // totals across attempts, which is the whole point of complete_count.

        // Reset all local states
        setAnswers({});
        setCompletedStepsPerCategory({});
        setVisitedCategories(new Set()); // Clear visited categories
        setMode('categoryIntro');
        setStepIndex(0);
        setHasStartedEditCategory(false);
        setHistory([]); // Clear history stack

        // Reset the previous attempt's RESULT data. Without this the new
        // attempt can show/share the old attempt's outcome: stale
        // workflowResult renders on a result page before the fresh workflow
        // returns, and a stale rehydrate key (userId:formId:categoryId has no
        // attempt component) blocks re-fetching the same category's result in
        // the new attempt.
        setWorkflowResult(null);
        workflowResultRef.current = null;
        lastRehydrateAttemptKeyRef.current = null;
        skipToFinalResultRef.current = false;
        setCompleteSurveyResults([]);
        setPersonaData([]);
        setYourNutrientData([]);
        setPersonaHtmlContent(null);
        setPersonaLabel('');
        setHasFetchedCompleteSurveyResults(false);
        setHasFetchedPersonaHtml(false);
        setHasFetchedPersonaData(false);
        setHasFetchedNutrientData(false);

        // Reset share state — shareUrl/OG image point at the OLD attempt's
        // execution result until the final page re-fetches them.
        setShareUrl('');
        setPreloadedImageBlob(null);
        setIsShareModalOpen(false);
        shareUrlFetchedRef.current = false;

        // Reset transient UI state
        setValidationErrorMessage(null);
        setIsProcessingNext(false);
        setIsNavigatingToResult(false);
        setLoadingWorkflowResult(false);
        questionShownAtRef.current = null;

        // Go to first category
        if (ordered.length > 0 && activeCategoryId !== ordered[0].id) {
            const params = new URLSearchParams(searchParams.toString());
            params.set('category', ordered[0].id);
            router.replace(`?${params.toString()}`, { scroll: false });
        }

        // Clear server-side progress and re-check for saved progress BEFORE
        // revealing the survey UI. This must be awaited and must happen
        // BEFORE onResumeDecisionMade (which hides the loading overlay) —
        // otherwise the user can start answering category 1 while the
        // wrapper's restart is still clearing old progress in the
        // background; if the user's fresh category-1 answers autosave
        // before that background work settles, the wrapper's re-check sees
        // "saved progress" and incorrectly shows the resume dialog mid-attempt.
        if (props.onRestartSurvey) {
            await props.onRestartSurvey();
        }

        // Hide resume content — only now that restart has fully completed.
        if (props.onResumeDecisionMade) {
            props.onResumeDecisionMade();
        }
    };

    const handleOptionalPrimaryAction = async () => {
        if (isAnsweredOptionalCategoryIntro) {
            if (shouldEditAnsweredOptionalCategory) {
                await onStartCategory();
                return;
            }

            onSkipOptionalCategory();
            return;
        }

        await onStartCategory();
    };

    return (
        <div
            ref={containerRef}
            id="category-page-container"
            // .category-page-dvh (survey-form.css) sets height:100dvh with a
            // 100vh fallback so it tracks the real mobile visible area instead of
            // the toolbar-hidden 100vh.
            className="category-page-dvh w-full overflow-hidden flex flex-col relative"
            style={{
                overscrollBehavior: 'none',
            }}
        >
            {/* Header Area */}
            {/* Full-screen Loading Overlay */}
            {(isProcessingNext ||
                loadingWorkflowResult ||
                isNavigatingToResult) && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100] flex items-center justify-center">
                    <div className="bg-white rounded-2xl p-8 shadow-2xl max-w-md text-center">
                        <SandyLoadingScreen
                            text={overlayLoadingCopy.text}
                            rotatingSubTexts={
                                overlayLoadingCopy.rotatingSubTexts
                            }
                        />
                    </div>
                </div>
            )}

            {/* Validation Error Message */}
            {validationErrorMessage && (
                <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-40 max-w-md w-full mx-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg">
                        <div className="flex justify-center items-center align-middle gap-3">
                            <div className="text-red-600 mt-1">
                                <svg
                                    className="w-5 h-5 self-center justify-center flex"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                            <div className="flex-1 align-middle">
                                <p className="text-red-800 font-medium text-sm">
                                    {validationErrorMessage}
                                </p>
                            </div>
                            <button
                                onClick={() => setValidationErrorMessage(null)}
                                className="text-red-600 hover:text-red-800 ml-2"
                                aria-label="Close error message"
                            >
                                <CloseIcon className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Header with close (X) button (fixed at top) */}
            <div
                ref={headerRef}
                id="category-page-header"
                className="fixed top-0 inset-x-0 w-full flex items-start justify-between py-3 md:px-20 px-5 z-50 backdrop-blur-md shadow-sm"
            >
                {/* Back button removed: back navigation now only via footer-back-button */}
                <span className="w-8 h-8" aria-hidden="true" />
                <picture id="category-page-header-logo">
                    <source
                        srcSet={`${getBaseUrl(
                            surveyForm?.headerLogoUrl || '',
                        )}.avif`}
                        type="image/avif"
                    />
                    <source
                        srcSet={`${getBaseUrl(
                            surveyForm?.headerLogoUrl || '',
                        )}.webp`}
                        type="image/webp"
                    />
                    <img
                        src={`${getBaseUrl(
                            surveyForm?.headerLogoUrl || '',
                        )}.png`}
                        width="51.6286"
                        height="50.3906"
                        className="object-contain w-full h-full"
                        style={
                            mode === 'categoryIntro'
                                ? {
                                      transform: `scale(${introScale})`,
                                      transformOrigin: 'center center',
                                      transition: 'transform 0.25s ease',
                                  }
                                : undefined
                        }
                        loading="eager"
                    />
                </picture>
                <a
                    href={surveyForm?.closeLinkPath || '/'}
                    aria-label="ปิด และกลับหน้าหลัก"
                    onClick={emitSurveyClose}
                >
                    <CloseIcon />
                </a>
            </div>
            {/* Spacer to offset fixed header height (approx 75px incl padding) */}
            <div
                id="category-page-header-spacer"
                style={{ height: headerHeight }}
                aria-hidden="true"
            />
            {/* Header Area */}

            {/* Main Content Area */}
            <div
                id="category-page-bg"
                className={`w-full flex-1 min-h-0 ${
                    mode === 'result' || mode === 'categoryIntro'
                        ? 'overflow-y-auto overflow-x-hidden'
                        : 'overflow-hidden'
                }`}
            >
                {isInitializing ? (
                    <div className="flex w-full h-full items-center justify-center">
                        <SandyLoadingScreen />
                    </div>
                ) : /* Show Resume Decision Content if flag is true */
                props.showResumeContent ? (
                    <ResumeDecisionContent
                        // Force a fresh instance whenever the decision screen changes
                        // (e.g. resume/edit dialog -> askLogin after "Start Over"). These
                        // are logically different screens; without a key React reuses the
                        // same instance across the mode change and its internal isLoading
                        // state (set true when a button is clicked, never reset back to
                        // false — that was designed for "unmounts right after") leaks into
                        // the next screen, showing it with everything disabled even though
                        // the new screen was never interacted with.
                        key={props.resumeDecisionMode || 'resume'}
                        slug={props.slug}
                        applicationId={surveyForm?.id || ''}
                        surveyName={surveyForm?.name}
                        userId={
                            effectiveUserId ||
                            props.userId ||
                            createdGuestId ||
                            undefined
                        }
                        isCompleted={props.isCompletedSurvey}
                        onContinue={handleContinueFromResume}
                        onRestart={handleRestartFromResume}
                        mode={props.resumeDecisionMode || 'resume'}
                        onSignUp={props.onSignUpFromAskLogin}
                        onContinueAsGuest={props.onContinueAsGuest}
                    />
                ) : (
                    <div
                        ref={contentRef}
                        id="category-page-content"
                        className={`w-full h-full min-h-0 flex flex-col items-center justify-center ${
                            mode === 'question'
                                ? ''
                                : isLastStep
                                  ? 'py-[20px] md:px-[120px] px-[30px]'
                                  : mode === 'result'
                                    ? 'pt-[20px] gap-[40px] md:gap-[80px]'
                                    : 'pt-[20px] md:pt-[40px] gap-[20px] md:gap-[40px]'
                        }`}
                        style={{
                            // Each mode handles its own scaling independently
                            // categoryIntro: uses introScale only
                            // question: uses pageScale only
                            // result: no scaling
                            transform:
                                mode === 'result'
                                    ? 'none'
                                    : mode === 'categoryIntro'
                                      ? `scale(${introScale})`
                                      : `scale(${pageScale})`,
                            transformOrigin: 'top center',
                            // Hide content until intro is measured to prevent flickering
                            opacity:
                                mode === 'categoryIntro' && !introReady ? 0 : 1,
                            visibility:
                                mode === 'categoryIntro' && !introReady
                                    ? 'hidden'
                                    : 'visible',
                        }}
                    >
                        {mode === 'categoryIntro' && (
                            <div
                                ref={introWrapperRef}
                                id="category-intro-wrapper"
                                className="flex flex-col items-center justify-center gap-2"
                                style={{
                                    // No additional transform needed - parent handles scaling
                                    transformOrigin: 'top center',
                                }}
                            >
                                <div
                                    id="category-page-body-container"
                                    className="w-full max-w-6xl grid grid-cols-3 md:gap-6 items-center px-4 md:px-8"
                                >
                                    <div
                                        id="category-button-container"
                                        data-side="left"
                                        className="flex flex-col gap-3"
                                    >
                                        {left.map((cat) => {
                                            const isActive =
                                                cat.id === activeCategoryId;
                                            const catIndex = ordered.findIndex(
                                                (c) => c.id === cat.id,
                                            );
                                            const totalQuestions =
                                                ordered[catIndex]?.questions
                                                    ?.length || 0;
                                            // Use completedStepsPerCategory instead of counting answered questions
                                            // This properly handles optional questions
                                            const completedSteps =
                                                completedStepsPerCategory[
                                                    cat.id
                                                ] || 0;
                                            const isCompleted =
                                                totalQuestions > 0 &&
                                                completedSteps >=
                                                    totalQuestions;
                                            // Check if this category can be clicked (visited or first category)
                                            const isVisited =
                                                visitedCategories.has(cat.id);
                                            const isClickable =
                                                isVisited || catIndex === 0;
                                            return (
                                                <div
                                                    key={cat.id}
                                                    id="category-button"
                                                    className={`
                                                    ${
                                                        isActive ? 'active' : ''
                                                    } ${
                                                        isCompleted
                                                            ? 'completed'
                                                            : ''
                                                    } ${
                                                        !isClickable
                                                            ? 'disabled'
                                                            : isCompleted
                                                              ? ''
                                                              : 'clickable'
                                                    }
                                                `}
                                                    style={{
                                                        cursor: isClickable
                                                            ? 'pointer'
                                                            : 'not-allowed',
                                                        opacity: !isClickable
                                                            ? 1
                                                            : 1,
                                                        pointerEvents:
                                                            isClickable
                                                                ? 'auto'
                                                                : 'none',
                                                    }}
                                                    onClick={() =>
                                                        isClickable &&
                                                        handleProgressBarClick(
                                                            catIndex,
                                                        )
                                                    }
                                                    title={
                                                        isClickable
                                                            ? `${
                                                                  props.lang ===
                                                                  'en'
                                                                      ? 'Go to'
                                                                      : 'ไปที่'
                                                              } ${getText(
                                                                  cat.name,
                                                                  cat.nameEn,
                                                              )}`
                                                            : props.lang ===
                                                                'en'
                                                              ? 'Not available yet'
                                                              : 'ยังไม่สามารถเลือกได้'
                                                    }
                                                >
                                                    <div
                                                        id="category-button-icon"
                                                        className={[
                                                            'flex items-center justify-center rounded-full text-[#0F8F80] text-sm font-semibold',
                                                            isActive
                                                                ? 'active'
                                                                : '',
                                                            isCompleted
                                                                ? 'completed'
                                                                : '',
                                                            isClickable &&
                                                            !isCompleted
                                                                ? 'clickable'
                                                                : '',
                                                        ].join(' ')}
                                                    >
                                                        {cat.iconUrl && (
                                                            <picture>
                                                                <source
                                                                    srcSet={`${
                                                                        cat.iconUrl ||
                                                                        ''
                                                                    }.avif`}
                                                                    type="image/avif"
                                                                />
                                                                <source
                                                                    srcSet={`${
                                                                        cat.iconUrl ||
                                                                        ''
                                                                    }.webp`}
                                                                    type="image/webp"
                                                                />
                                                                <img
                                                                    src={`${cat.iconUrl}.png`}
                                                                    alt="icon"
                                                                    width={28}
                                                                    height={28}
                                                                    loading="eager"
                                                                />
                                                            </picture>
                                                        )}
                                                    </div>
                                                    <span
                                                        id="category-button-name"
                                                        className={[
                                                            'text-[14px] md:text-[15px] leading-snug',
                                                            isActive
                                                                ? 'active'
                                                                : '',
                                                        ].join(' ')}
                                                    >
                                                        {getText(
                                                            cat.name,
                                                            cat.nameEn,
                                                        )}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div
                                        id="mascot-area-container"
                                        className="relative flex items-center justify-center"
                                    >
                                        <div
                                            id="mascot-area-container-bg"
                                            className={`w-[400px] h-[400px] rounded-full flex items-center justify-center ${
                                                shouldRenderOptionalCard
                                                    ? 'px-[20px] py-[20px]'
                                                    : ''
                                            }`}
                                        >
                                            <div
                                                id="mascot-area-container-picture"
                                                className="relative w-full h-full"
                                            >
                                                <picture
                                                    id="category-mascot-image"
                                                    className="w-full h-full md:flex hidden items-center justify-center"
                                                >
                                                    <source
                                                        srcSet={`${getBaseUrl(
                                                            activeCategory?.mascotImageUrl ||
                                                                '',
                                                        )}.avif`}
                                                        type="image/avif"
                                                    />
                                                    <source
                                                        srcSet={`${getBaseUrl(
                                                            activeCategory?.mascotImageUrl ||
                                                                '',
                                                        )}.webp`}
                                                        type="image/webp"
                                                    />
                                                    <img
                                                        src={`${getBaseUrl(
                                                            activeCategory?.mascotImageUrl ||
                                                                '',
                                                        )}.png`}
                                                        width="155"
                                                        height="376"
                                                        className="object-contain w-full h-full"
                                                        loading="eager"
                                                    />
                                                </picture>
                                                <picture
                                                    id="category-mascot-image"
                                                    className="w-full h-full md:hidden flex items-center justify-center"
                                                >
                                                    <source
                                                        srcSet={`${getBaseUrl(
                                                            activeCategory?.mascotMobileImageUrl ||
                                                                '',
                                                        )}.avif`}
                                                        type="image/avif"
                                                    />
                                                    <source
                                                        srcSet={`${getBaseUrl(
                                                            activeCategory?.mascotMobileImageUrl ||
                                                                '',
                                                        )}.webp`}
                                                        type="image/webp"
                                                    />
                                                    <img
                                                        src={`${getBaseUrl(
                                                            activeCategory?.mascotMobileImageUrl ||
                                                                '',
                                                        )}.png`}
                                                        width="155"
                                                        height="376"
                                                        className="object-contain w-full h-full"
                                                        loading="eager"
                                                    />
                                                </picture>
                                                {/* Show optional when current user category is on it's step */}
                                                {shouldRenderOptionalCard &&
                                                    optionalCategory &&
                                                    (() => {
                                                        const isActive =
                                                            optionalCategory.id ===
                                                            activeCategoryId;
                                                        const catIndex =
                                                            ordered.findIndex(
                                                                (c) =>
                                                                    c.id ===
                                                                    optionalCategory.id,
                                                            );
                                                        const totalQuestions =
                                                            ordered[catIndex]
                                                                ?.questions
                                                                ?.length || 0;
                                                        const completedSteps =
                                                            completedStepsPerCategory[
                                                                optionalCategory
                                                                    .id
                                                            ] || 0;
                                                        const isCompleted =
                                                            totalQuestions >
                                                                0 &&
                                                            completedSteps >=
                                                                totalQuestions;
                                                        const isVisited =
                                                            visitedCategories.has(
                                                                optionalCategory.id,
                                                            );
                                                        // DF #111: navigation
                                                        // gate, so a resumed
                                                        // session can open it.
                                                        const isClickable =
                                                            isVisited ||
                                                            catIndex === 0 ||
                                                            isActive ||
                                                            canRevisitAnsweredCategories;

                                                        return (
                                                            <div
                                                                id="category-button"
                                                                className={`optional-center-card ${
                                                                    isActive
                                                                        ? 'active'
                                                                        : ''
                                                                } ${
                                                                    isCompleted
                                                                        ? 'completed'
                                                                        : ''
                                                                } ${
                                                                    !isClickable
                                                                        ? 'disabled'
                                                                        : isCompleted
                                                                          ? ''
                                                                          : 'clickable'
                                                                }`}
                                                                style={{
                                                                    cursor: isClickable
                                                                        ? 'pointer'
                                                                        : 'not-allowed',
                                                                    pointerEvents:
                                                                        isClickable
                                                                            ? 'auto'
                                                                            : 'none',
                                                                }}
                                                                onClick={() =>
                                                                    isClickable &&
                                                                    handleProgressBarClick(
                                                                        catIndex,
                                                                    )
                                                                }
                                                                title={
                                                                    isClickable
                                                                        ? `${
                                                                              props.lang ===
                                                                              'en'
                                                                                  ? 'Go to'
                                                                                  : 'ไปที่'
                                                                          } ${getText(
                                                                              optionalCategory.name,
                                                                              optionalCategory.nameEn,
                                                                          )}`
                                                                        : props.lang ===
                                                                            'en'
                                                                          ? 'Not available yet'
                                                                          : 'ยังไม่สามารถเลือกได้'
                                                                }
                                                            >
                                                                <div
                                                                    id="category-button-icon"
                                                                    className={[
                                                                        'flex items-center justify-center rounded-full text-[#0F8F80] text-sm font-semibold',
                                                                        isActive
                                                                            ? 'active'
                                                                            : '',
                                                                        isCompleted
                                                                            ? 'completed'
                                                                            : '',
                                                                        isClickable &&
                                                                        !isCompleted
                                                                            ? 'clickable'
                                                                            : '',
                                                                    ].join(' ')}
                                                                >
                                                                    {optionalCategory.iconUrl && (
                                                                        <picture id="category-button-icon-picture">
                                                                            <source
                                                                                srcSet={`${
                                                                                    optionalCategory.iconUrl ||
                                                                                    ''
                                                                                }.avif`}
                                                                                type="image/avif"
                                                                            />
                                                                            <source
                                                                                srcSet={`${
                                                                                    optionalCategory.iconUrl ||
                                                                                    ''
                                                                                }.webp`}
                                                                                type="image/webp"
                                                                            />
                                                                            <img
                                                                                src={`${optionalCategory.iconUrl}.png`}
                                                                                alt="icon"
                                                                                width={
                                                                                    22
                                                                                }
                                                                                height={
                                                                                    22
                                                                                }
                                                                                loading="eager"
                                                                            />
                                                                        </picture>
                                                                    )}
                                                                </div>
                                                                <span
                                                                    id="category-button-name"
                                                                    className={[
                                                                        'text-[14px] md:text-[15px] leading-snug',
                                                                        isActive
                                                                            ? 'active'
                                                                            : '',
                                                                    ].join(' ')}
                                                                >
                                                                    {getText(
                                                                        optionalCategory.name,
                                                                        optionalCategory.nameEn,
                                                                    )}
                                                                </span>
                                                            </div>
                                                        );
                                                    })()}
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        id="category-button-container"
                                        data-side="right"
                                        className="flex flex-col gap-3"
                                    >
                                        {right.map((cat) => {
                                            const isActive =
                                                cat.id === activeCategoryId;
                                            const catIndex = ordered.findIndex(
                                                (c) => c.id === cat.id,
                                            );
                                            const totalQuestions =
                                                ordered[catIndex]?.questions
                                                    ?.length || 0;
                                            // Use completedStepsPerCategory instead of counting answered questions
                                            // This properly handles optional questions
                                            const completedSteps =
                                                completedStepsPerCategory[
                                                    cat.id
                                                ] || 0;
                                            const isCompleted =
                                                totalQuestions > 0 &&
                                                completedSteps >=
                                                    totalQuestions;
                                            // Check if this category can be clicked (visited or first category)
                                            const isVisited =
                                                visitedCategories.has(cat.id);
                                            const isClickable =
                                                isVisited || catIndex === 0;
                                            return (
                                                <div
                                                    key={cat.id}
                                                    id="category-button"
                                                    className={`
                                                    ${
                                                        isActive ? 'active' : ''
                                                    } ${
                                                        isCompleted
                                                            ? 'completed'
                                                            : ''
                                                    } ${
                                                        !isClickable
                                                            ? 'disabled'
                                                            : isCompleted
                                                              ? ''
                                                              : 'clickable'
                                                    }
                                                `}
                                                    style={{
                                                        cursor: isClickable
                                                            ? 'pointer'
                                                            : 'not-allowed',
                                                        opacity: !isClickable
                                                            ? 1
                                                            : 1,
                                                        pointerEvents:
                                                            isClickable
                                                                ? 'auto'
                                                                : 'none',
                                                    }}
                                                    onClick={() =>
                                                        isClickable &&
                                                        handleProgressBarClick(
                                                            catIndex,
                                                        )
                                                    }
                                                    title={
                                                        isClickable
                                                            ? `${
                                                                  props.lang ===
                                                                  'en'
                                                                      ? 'Go to'
                                                                      : 'ไปที่'
                                                              } ${getText(
                                                                  cat.name,
                                                                  cat.nameEn,
                                                              )}`
                                                            : props.lang ===
                                                                'en'
                                                              ? 'Not available yet'
                                                              : 'ยังไม่สามารถเลือกได้'
                                                    }
                                                >
                                                    <div
                                                        id="category-button-icon"
                                                        className={[
                                                            'flex items-center justify-center rounded-full text-[#0F8F80] text-sm font-semibold',
                                                            isActive
                                                                ? 'active'
                                                                : '',
                                                            isCompleted
                                                                ? 'completed'
                                                                : '',
                                                            isClickable &&
                                                            !isCompleted
                                                                ? 'clickable'
                                                                : '',
                                                        ].join(' ')}
                                                    >
                                                        {cat.iconUrl && (
                                                            <picture id="category-button-icon-picture">
                                                                <source
                                                                    srcSet={`${
                                                                        cat.iconUrl ||
                                                                        ''
                                                                    }.avif`}
                                                                    type="image/avif"
                                                                />
                                                                <source
                                                                    srcSet={`${
                                                                        cat.iconUrl ||
                                                                        ''
                                                                    }.webp`}
                                                                    type="image/webp"
                                                                />
                                                                <img
                                                                    src={`${cat.iconUrl}.png`}
                                                                    alt="icon"
                                                                    width={28}
                                                                    height={28}
                                                                    loading="eager"
                                                                />
                                                            </picture>
                                                        )}
                                                    </div>
                                                    <span
                                                        id="category-button-name"
                                                        className={[
                                                            'text-[14px] md:text-[15px] leading-snug',
                                                            isActive
                                                                ? 'active'
                                                                : '',
                                                        ].join(' ')}
                                                    >
                                                        {getText(
                                                            cat.name,
                                                            cat.nameEn,
                                                        )}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div
                                    id="category-detail-info"
                                    className="w-full flex flex-col items-center gap-[16px] pb-[16px]"
                                >
                                    <div
                                        id="category-title-area"
                                        className="flex flex-col justify-center w-full pt-[20px] gap-[16px] items-center"
                                    >
                                        <h3 className="items-center font-medium text-[#2A807B] text-sm md:text-base">
                                            {t('survey.atCategory')}{' '}
                                            {activeIndex + 1}
                                        </h3>
                                        {/* DF-17: responsive title — large Thai category
                                            names (e.g. "ผลตรวจสุขภาพทางการแพทย์") used the
                                            browser-default h1 size (~32px) and, combined with
                                            the body grid + description, overflowed the viewport
                                            on 320–640px screens, pushing the description/button
                                            off-screen. Scale down on small widths and allow the
                                            text to wrap inside the available width. */}
                                        <h1 className="text-center px-2 text-[22px] sm:text-[26px] md:text-[32px] leading-tight break-words max-w-full">
                                            <strong>
                                                {getText(
                                                    activeCategory?.name,
                                                    activeCategory?.nameEn,
                                                )}
                                            </strong>
                                        </h1>
                                        {isOptionalCategoryIntro &&
                                            !isAnsweredOptionalCategoryIntro && (
                                                <p className="max-w-[680px] px-4 text-center text-[13px] sm:text-[14px] md:text-[15px] leading-[1.55] text-[#3E4C59]">
                                                    {t(
                                                        'survey_form.category_page.optional_category_description',
                                                        'คุณสนใจกรอกข้อมูลผลตรวจสุขภาพ เพื่อให้ได้วิตามินที่ตรงกับคุณมากขึ้น?',
                                                    )}
                                                </p>
                                            )}
                                    </div>
                                </div>
                                {shouldShowInlineCategoryIntroButton && (
                                    <button
                                        className="z-9999"
                                        id="start-survey-button"
                                        onClick={onStartCategory}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                onStartCategory();
                                            }
                                        }}
                                    >
                                        <p className="text-[16px]">
                                            {categoryIntroPrimaryButtonText}
                                        </p>
                                    </button>
                                )}
                            </div>
                        )}

                        {mode === 'question' && (
                            <div
                                className="w-full h-full min-h-0 flex flex-col items-center justify-center"
                                style={{ overflow: 'hidden' }}
                            >
                                <QuestionFormat
                                    step={stepIndex + 1}
                                    totalSteps={activeTotalSteps}
                                    totalStepsPerCategory={ordered.map(
                                        (cat) => cat.questions?.length || 0,
                                    )}
                                    questionText={
                                        activeQuestions?.[stepIndex]
                                            ?.questionText || ''
                                    }
                                    questionType={
                                        activeQuestions?.[stepIndex]
                                            ?.questionType || 'multiselect'
                                    }
                                    options={
                                        activeQuestions?.[stepIndex]
                                            ?.questionType !== 'text'
                                            ? activeQuestions?.[stepIndex]
                                                  ?.questionConfig?.options ||
                                              []
                                            : []
                                    }
                                    fields={
                                        activeQuestions?.[stepIndex]
                                            ?.questionType === 'slider'
                                            ? activeQuestions?.[stepIndex]
                                                  ?.questionConfig?.fields ||
                                              activeQuestions?.[stepIndex]
                                                  ?.questionConfig?.sliders ||
                                              []
                                            : []
                                    }
                                    rows={
                                        activeQuestions?.[stepIndex]
                                            ?.questionType === 'matrix'
                                            ? activeQuestions?.[stepIndex]
                                                  ?.questionConfig?.rows || []
                                            : []
                                    }
                                    columns={
                                        activeQuestions?.[stepIndex]
                                            ?.questionType === 'matrix'
                                            ? activeQuestions?.[stepIndex]
                                                  ?.questionConfig?.columns ||
                                              []
                                            : []
                                    }
                                    placeholder={
                                        activeQuestions?.[stepIndex]
                                            ?.questionType === 'text'
                                            ? activeQuestions?.[stepIndex]
                                                  ?.questionConfig
                                                  ?.placeholder || ''
                                            : undefined
                                    }
                                    value={currentAnswer?.values}
                                    onChange={(vals) =>
                                        updateCurrentAnswer(vals)
                                    }
                                    totalCategories={ordered.length}
                                    activeCategoryIndex={activeIndex}
                                    activeCategoryName={getText(
                                        activeCategory?.name,
                                        activeCategory?.nameEn,
                                    )}
                                    completedStepsPerCategory={ordered.map(
                                        (cat) =>
                                            completedStepsPerCategory[cat.id] ||
                                            0,
                                    )}
                                    visitedCategoryIndices={ordered
                                        .map((cat, idx) =>
                                            visitedCategories.has(cat.id)
                                                ? idx
                                                : -1,
                                        )
                                        .filter((idx) => idx !== -1)}
                                    mascotImageUrl={
                                        activeCategory?.mascotImageUrl || null
                                    }
                                    mascotMobileImageUrl={
                                        activeCategory?.mascotMobileImageUrl ||
                                        null
                                    }
                                    mascotAlt={
                                        getText(
                                            activeCategory?.name,
                                            activeCategory?.nameEn,
                                        )
                                            ? `${getText(
                                                  activeCategory?.name,
                                                  activeCategory?.nameEn,
                                              )} mascot`
                                            : 'category mascot'
                                    }
                                    onProgressBarClick={handleProgressBarClick}
                                    layout={
                                        activeQuestions?.[stepIndex]
                                            ?.questionConfig?.layout
                                    }
                                    maxSelections={
                                        activeQuestions?.[stepIndex]
                                            ?.questionConfig?.maxSelections
                                    }
                                    minSelections={
                                        activeQuestions?.[stepIndex]
                                            ?.questionConfig?.minSelections
                                    }
                                    showCounter={
                                        activeQuestions?.[stepIndex]
                                            ?.questionConfig?.showCounter
                                    }
                                    showImages={
                                        activeQuestions?.[stepIndex]
                                            ?.questionConfig?.showImages
                                    }
                                    multiline={
                                        activeQuestions?.[stepIndex]
                                            ?.questionConfig?.multiline
                                    }
                                    otherConfig={
                                        activeQuestions?.[stepIndex]
                                            ?.questionConfig
                                    }
                                    enableRanking={
                                        activeQuestions?.[stepIndex]
                                            ?.questionType === 'checkbox'
                                            ? activeQuestions?.[stepIndex]
                                                  ?.questionConfig
                                                  ?.enableRanking
                                            : undefined
                                    }
                                    rankingLimit={
                                        activeQuestions?.[stepIndex]
                                            ?.questionType === 'checkbox'
                                            ? activeQuestions?.[stepIndex]
                                                  ?.questionConfig?.rankingLimit
                                            : undefined
                                    }
                                    consentText={
                                        activeQuestions?.[stepIndex]
                                            ?.questionType === 'consent'
                                            ? activeQuestions?.[stepIndex]
                                                  ?.questionConfig?.consentText
                                            : undefined
                                    }
                                    consentTextEn={
                                        activeQuestions?.[stepIndex]
                                            ?.questionType === 'consent'
                                            ? activeQuestions?.[stepIndex]
                                                  ?.questionConfig
                                                  ?.consentTextEn
                                            : undefined
                                    }
                                    consentCheckboxes={
                                        activeQuestions?.[stepIndex]
                                            ?.questionType === 'consent'
                                            ? activeQuestions?.[stepIndex]
                                                  ?.questionConfig?.checkboxes
                                            : undefined
                                    }
                                    userEmail={props.userEmail}
                                    validationRules={
                                        activeQuestions?.[stepIndex]
                                            ?.questionConfig?.validationRules
                                    }
                                    onEnterPress={onNextQuestion}
                                    isOptionalCategory={
                                        activeCategory?.isOptional === true
                                    }
                                />
                                {/* Spacer to prevent content from being covered by fixed footer */}
                                {/* Footer fixed; spacer removed for no-scroll layout. Scale question if needed */}
                            </div>
                        )}

                        {mode === 'result' && (
                            <div
                                className={`w-full flex-1 ${
                                    showShareButtons
                                        ? 'h-full'
                                        : 'max-w-5xl mx-auto'
                                } p-0 flex flex-col items-center gap-8`}
                            >
                                {loadingWorkflowResult ? (
                                    <div className="flex w-full h-full items-center justify-center">
                                        <SandyLoadingScreen
                                            text={
                                                CATEGORY_PAGE_LOADING_COPY
                                                    .persona.text
                                            }
                                            rotatingSubTexts={
                                                CATEGORY_PAGE_LOADING_COPY
                                                    .persona.rotatingSubTexts
                                            }
                                        />
                                    </div>
                                ) : workflowResult ? (
                                    // Render workflow result from database
                                    <div className="p-8 flex items-center justify-center w-full">
                                        <WorkflowResultDisplay
                                            executionResult={
                                                workflowResult.executionResult
                                            }
                                            resultConfig={
                                                workflowResult.resultConfig
                                            }
                                            locale={getLocale()}
                                            categoryName={getText(
                                                activeCategory?.name,
                                                activeCategory?.nameEn,
                                            )}
                                            categoryIndex={activeIndex}
                                            onBack={() => {
                                                // Navigate back to category intro (progress homepage)
                                                handleProgressBarClick(
                                                    activeIndex,
                                                );
                                            }}
                                            onNext={onNextFromResult}
                                        />
                                    </div>
                                ) : isLastStep ? (
                                    isFinalInsightsLoading ? (
                                        <div className="flex w-full h-full items-center justify-center">
                                            <SandyLoadingScreen
                                                text={
                                                    CATEGORY_PAGE_LOADING_COPY
                                                        .avenger.text
                                                }
                                                rotatingSubTexts={
                                                    CATEGORY_PAGE_LOADING_COPY
                                                        .avenger
                                                        .rotatingSubTexts
                                                }
                                            />
                                        </div>
                                    ) : (
                                        <div className="w-full h-full flex flex-col gap-4">
                                            <div className="w-full flex min-[480px]:flex-row flex-col items-center min-[480px]:justify-end gap-2.5">
                                                <span className="text-[13px] text-[#2A807B]">
                                                    {t(
                                                        'survey_form.category_page.share_results',
                                                    )}
                                                </span>
                                                {isMobile !== false ? (
                                                    <button
                                                        onClick={() =>
                                                            setIsShareModalOpen(
                                                                true,
                                                            )
                                                        }
                                                        className="bg-[#2A807B] rounded-full px-10 h-14 flex items-center justify-center gap-2 hover:bg-[#1f6663] transition-colors cursor-pointer text-white text-[15px] font-semibold shadow-md"
                                                    >
                                                        {isLoadingShareUrl ? (
                                                            <svg
                                                                className="animate-spin h-4 w-4 text-white"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                fill="none"
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <circle
                                                                    className="opacity-25"
                                                                    cx="12"
                                                                    cy="12"
                                                                    r="10"
                                                                    stroke="currentColor"
                                                                    strokeWidth="4"
                                                                ></circle>
                                                                <path
                                                                    className="opacity-75"
                                                                    fill="currentColor"
                                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                                ></path>
                                                            </svg>
                                                        ) : (
                                                            <svg
                                                                className="w-4 h-4"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth={
                                                                        2
                                                                    }
                                                                    d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                                                                />
                                                            </svg>
                                                        )}
                                                        {t(
                                                            'survey_form.category_page.share_results',
                                                        )}
                                                    </button>
                                                ) : (
                                                    <div className="w-fit flex items-center justify-center gap-1">
                                                        <a
                                                            href={
                                                                shareUrl
                                                                    ? `https://www.facebook.com/sharer.php?u=${encodeURIComponent(
                                                                          `${shareUrl}${
                                                                              shareUrl.includes(
                                                                                  '?',
                                                                              )
                                                                                  ? '&'
                                                                                  : '?'
                                                                          }t=${dayjs().unix()}`,
                                                                      )}`
                                                                    : '#'
                                                            }
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="bg-[#92C8B7] rounded-full w-8 h-8 flex items-center justify-center hover:bg-[#7AB5A3] transition-colors cursor-pointer"
                                                            title="Share on Facebook"
                                                            onClick={(e) => {
                                                                if (!shareUrl)
                                                                    e.preventDefault();
                                                            }}
                                                        >
                                                            <img
                                                                src="/assets/icons/facebook.svg"
                                                                alt="Facebook"
                                                                width={18}
                                                                height={18}
                                                                className="inline-block"
                                                            />
                                                        </a>
                                                        <button
                                                            onClick={async () => {
                                                                if (!shareUrl)
                                                                    return;
                                                                try {
                                                                    await copyToClipboard(
                                                                        shareUrl,
                                                                    );
                                                                    toast.success(
                                                                        'คัดลอกลิงก์แล้ว! นำไปแชร์ใน Instagram ได้เลย',
                                                                        {
                                                                            position:
                                                                                'bottom-center',
                                                                        },
                                                                    );
                                                                } catch (err) {
                                                                    toast.error(
                                                                        'ไม่สามารถคัดลอกลิงก์ได้',
                                                                        {
                                                                            position:
                                                                                'bottom-center',
                                                                        },
                                                                    );
                                                                }
                                                            }}
                                                            disabled={
                                                                !shareUrl &&
                                                                !isLoadingShareUrl
                                                            }
                                                            className="bg-[#92C8B7] rounded-full w-8 h-8 flex items-center justify-center hover:bg-[#7AB5A3] transition-colors cursor-pointer disabled:opacity-50 relative"
                                                            title="Copy URL for Instagram"
                                                        >
                                                            {isLoadingShareUrl ? (
                                                                <svg
                                                                    className="animate-spin h-4 w-4 text-white"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    fill="none"
                                                                    viewBox="0 0 24 24"
                                                                >
                                                                    <circle
                                                                        className="opacity-25"
                                                                        cx="12"
                                                                        cy="12"
                                                                        r="10"
                                                                        stroke="currentColor"
                                                                        strokeWidth="4"
                                                                    ></circle>
                                                                    <path
                                                                        className="opacity-75"
                                                                        fill="currentColor"
                                                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                                    ></path>
                                                                </svg>
                                                            ) : (
                                                                <img
                                                                    src="/assets/icons/instagram.svg"
                                                                    alt="Instagram"
                                                                    width={18}
                                                                    height={18}
                                                                    className="inline-block"
                                                                />
                                                            )}
                                                        </button>
                                                        <button
                                                            onClick={async () => {
                                                                if (!shareUrl)
                                                                    return;
                                                                try {
                                                                    await copyToClipboard(
                                                                        shareUrl,
                                                                    );
                                                                    toast.success(
                                                                        'คัดลอกลิงก์แล้ว! นำไปแชร์ใน TikTok ได้เลย',
                                                                        {
                                                                            position:
                                                                                'bottom-center',
                                                                        },
                                                                    );
                                                                } catch (err) {
                                                                    toast.error(
                                                                        'ไม่สามารถคัดลอกลิงก์ได้',
                                                                        {
                                                                            position:
                                                                                'bottom-center',
                                                                        },
                                                                    );
                                                                }
                                                            }}
                                                            disabled={
                                                                !shareUrl &&
                                                                !isLoadingShareUrl
                                                            }
                                                            className="bg-[#92C8B7] rounded-full w-8 h-8 flex items-center justify-center hover:bg-[#7AB5A3] transition-colors cursor-pointer disabled:opacity-50 relative"
                                                            title="Copy URL for TikTok"
                                                        >
                                                            {isLoadingShareUrl ? (
                                                                <svg
                                                                    className="animate-spin h-4 w-4 text-white"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    fill="none"
                                                                    viewBox="0 0 24 24"
                                                                >
                                                                    <circle
                                                                        className="opacity-25"
                                                                        cx="12"
                                                                        cy="12"
                                                                        r="10"
                                                                        stroke="currentColor"
                                                                        strokeWidth="4"
                                                                    ></circle>
                                                                    <path
                                                                        className="opacity-75"
                                                                        fill="currentColor"
                                                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                                    ></path>
                                                                </svg>
                                                            ) : (
                                                                <img
                                                                    src="/assets/icons/tiktok.svg"
                                                                    alt="TikTok"
                                                                    width={18}
                                                                    height={18}
                                                                    className="inline-block"
                                                                />
                                                            )}
                                                        </button>
                                                        <a
                                                            href={
                                                                shareUrl
                                                                    ? `https://line.me/R/msg/text/?${encodeURIComponent(
                                                                          `${shareUrl}${
                                                                              shareUrl.includes(
                                                                                  '?',
                                                                              )
                                                                                  ? '&'
                                                                                  : '?'
                                                                          }t=${dayjs().unix()}`,
                                                                      )}`
                                                                    : '#'
                                                            }
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="bg-[#92C8B7] rounded-full w-8 h-8 flex items-center justify-center hover:bg-[#7AB5A3] transition-colors cursor-pointer"
                                                            title="Share on LINE"
                                                            onClick={(e) => {
                                                                if (!shareUrl)
                                                                    e.preventDefault();
                                                            }}
                                                        >
                                                            <img
                                                                src="/assets/icons/line.svg"
                                                                alt="LINE"
                                                                width={18}
                                                                height={18}
                                                                className="inline-block"
                                                            />
                                                        </a>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="w-full flex flex-col lg:flex-row lg:justify-center lg:items-start items-center text-center gap-5 pb-16 lg:pb-0">
                                                <div className="w-full lg:w-2/5 flex justify-start flex-col">
                                                    {personaHtmlContent ? (
                                                        <div
                                                            dangerouslySetInnerHTML={{
                                                                __html: personaHtmlContent,
                                                            }}
                                                            className="persona-content"
                                                        />
                                                    ) : (
                                                        <div className="w-full flex items-center justify-center py-4">
                                                            <SandyLoadingScreen
                                                                text={
                                                                    CATEGORY_PAGE_LOADING_COPY
                                                                        .persona
                                                                        .text
                                                                }
                                                                rotatingSubTexts={
                                                                    CATEGORY_PAGE_LOADING_COPY
                                                                        .persona
                                                                        .rotatingSubTexts
                                                                }
                                                                size={160}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="w-full lg:w-3/5 flex flex-col gap-3">
                                                    <div className="w-full flex min-[512px]:flex-row flex-col items-center min-[512px]:justify-between gap-2.5">
                                                        <h3 className="font-semibold flex-shrink-0 text-[20px]">
                                                            {t(
                                                                'survey_form.category_page.vitamin_deficiency_trend',
                                                            )}
                                                        </h3>
                                                        <div className="flex-shrink-0">
                                                            <div className="bg-white/45 rounded-full px-[14px] py-[6px] flex items-center gap-2">
                                                                <div className="text-[#2e2e2e] font-semibold text-[10px] whitespace-nowrap">
                                                                    {t(
                                                                        'survey_form.category_page.lack_a_lot',
                                                                    )}
                                                                </div>
                                                                <div className="flex w-9 h-1 rounded-full overflow-hidden">
                                                                    <div
                                                                        style={{
                                                                            background:
                                                                                '#dc3030',
                                                                        }}
                                                                        className="flex-1"
                                                                    ></div>
                                                                    <div
                                                                        style={{
                                                                            background:
                                                                                '#ffc727',
                                                                        }}
                                                                        className="flex-1"
                                                                    ></div>
                                                                    <div
                                                                        style={{
                                                                            background:
                                                                                '#2a807b',
                                                                        }}
                                                                        className="flex-1"
                                                                    ></div>
                                                                </div>
                                                                <div className="text-[#2e2e2e] font-semibold text-[10px] whitespace-nowrap">
                                                                    {t(
                                                                        'survey_form.category_page.sufficient',
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="w-full flex flex-col gap-6">
                                                        <ChartResult
                                                            resultData={
                                                                vitaminScores
                                                            }
                                                        />
                                                        <div className="w-full flex pb-5">
                                                            <ResultCardList
                                                                personaData={
                                                                    personaData
                                                                }
                                                                categoryTitles={ordered.map(
                                                                    (cat) =>
                                                                        getText(
                                                                            cat.name,
                                                                            cat.nameEn,
                                                                        ),
                                                                )}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                ) : (
                                    // Default render for non-last step - show result with navigation
                                    <div className="p-4 md:p-6 flex flex-col items-center justify-center w-full gap-6">
                                        {/* Category completion message */}
                                        <div className="text-center">
                                            <h2 className="text-2xl md:text-3xl font-bold text-[#0F8F80] mb-2">
                                                {t(
                                                    'survey_form.category_page.completed',
                                                )}{' '}
                                                {getText(
                                                    activeCategory?.name,
                                                    activeCategory?.nameEn,
                                                )}
                                            </h2>
                                            <p className="text-gray-600">
                                                {t(
                                                    'survey_form.category_page.completed_category',
                                                )}
                                            </p>
                                        </div>

                                        {/* Workflow result display (if available) */}
                                        {loadingWorkflowResult && (
                                            <SandyLoadingScreen
                                                text={
                                                    CATEGORY_PAGE_LOADING_COPY
                                                        .persona.text
                                                }
                                                rotatingSubTexts={
                                                    CATEGORY_PAGE_LOADING_COPY
                                                        .persona
                                                        .rotatingSubTexts
                                                }
                                                size={160}
                                            />
                                        )}
                                        {!loadingWorkflowResult &&
                                            workflowResult && (
                                                <div className="w-full max-w-2xl">
                                                    <WorkflowResultDisplay
                                                        executionResult={
                                                            workflowResult
                                                        }
                                                        resultConfig={
                                                            workflowResult
                                                        }
                                                        locale={getLocale()}
                                                    />
                                                </div>
                                            )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
            {/* Main Content Area */}

            {/* Spacer to avoid content hidden behind fixed footer */}
            {!workflowResult && showFixedFooter && (
                <div id="footer-button-spacer" aria-hidden="true" />
            )}

            {/*------------------footer---------------------*/}
            {!hasResumeOverlay && mode === 'question' && (
                <div
                    ref={footerRef}
                    id="footer-button-container"
                    className="flex items-center gap-4"
                >
                    {/* Footer Back button now sits to the left of Next button */}
                    <button
                        id="footer-back-button"
                        type="button"
                        disabled={isProcessingNext}
                        aria-disabled={isProcessingNext}
                        onClick={() => {
                            // In question mode: go back one step or to intro
                            if (history.length > 0) {
                                goBack();
                                return;
                            }
                            onBackQuestion();
                        }}
                        aria-label={t('survey_form.category_page.back')}
                        className="flex flex-row items-center justify-center gap-2"
                    >
                        <WestIcon
                            style={{
                                color: '#2A807B',
                                width: '18px',
                                height: '18px',
                            }}
                        />{' '}
                        <p className="text-[16px] text-[#2A807B] font-semibold">
                            {t('survey_form.category_page.back')}
                        </p>
                    </button>
                    {(() => {
                        const currentQuestion = activeQuestions?.[stepIndex];
                        const vals = currentAnswer?.values || [];

                        // Check if question is required - default to FALSE (optional)
                        // This means questions are optional by default unless explicitly marked as required
                        // Check both question-level required flag and questionConfig.isRequired
                        const isRequired =
                            currentQuestion?.required === true ||
                            currentQuestion?.questionConfig?.isRequired ===
                                true;

                        // console.log('🔘 Button State Check:', {
                        //     step: stepIndex + 1,
                        //     questionText: currentQuestion?.questionText?.substring(0, 50) + '...',
                        //     questionType: currentQuestion?.questionType,
                        //     isRequired: isRequired,
                        //     requiredRaw: currentQuestion?.required,
                        //     hasValues: vals.length > 0,
                        //     valuesCount: vals.length,
                        // });

                        const isAnswered = (() => {
                            if (!currentQuestion) {
                                // console.log('  ✗ No current question');
                                return false;
                            }

                            // Consent questions - check metadata for canProceed status
                            if (currentQuestion.questionType === 'consent') {
                                // Consent answer structure includes a scroll marker '__scrolled__'
                                // and checkbox IDs for checked boxes
                                if (vals.length === 0) {
                                    // console.log('  📋 Consent (ALWAYS required): NO DATA ✗');
                                    return false;
                                }

                                const checkboxes =
                                    currentQuestion.questionConfig
                                        ?.checkboxes || [];

                                // If no checkboxes, just check if scrolled
                                if (checkboxes.length === 0) {
                                    const hasScrolled =
                                        vals.includes('__scrolled__');
                                    // console.log('  📋 Consent (no checkboxes):', hasScrolled ? 'SCROLLED ✓' : 'NOT SCROLLED ✗');
                                    return hasScrolled;
                                }

                                const requiredCheckboxIds = checkboxes
                                    .filter((cb: any) => cb.isRequired)
                                    .map((cb: any) => cb.id);

                                // Check if scroll marker exists
                                const hasScrolled =
                                    vals.includes('__scrolled__');

                                // Check if all required checkboxes are in vals array (excluding marker)
                                const actualCheckboxIds = vals.filter(
                                    (v) => !v.startsWith('__'),
                                );
                                const allRequiredChecked =
                                    requiredCheckboxIds.length === 0 ||
                                    requiredCheckboxIds.every((id: string) =>
                                        actualCheckboxIds.includes(id),
                                    );

                                // Must both scroll to bottom AND check all required boxes
                                // console.log('  📋 Consent (ALWAYS required):', (hasScrolled && allRequiredChecked) ? 'COMPLETE ✓' : 'INCOMPLETE ✗',
                                //     `(scrolled: ${hasScrolled}, required: ${requiredCheckboxLabels.length}, checked: ${allRequiredChecked})`);
                                return hasScrolled && allRequiredChecked;
                            }

                            // Radio questions are ALWAYS required - no need to check isRequired flag
                            if (currentQuestion.questionType === 'radio') {
                                const hasSelection = vals.length > 0;
                                // console.log('  🔘 Radio (ALWAYS required):', hasSelection ? 'SELECTED ✓' : 'NOT SELECTED ✗');
                                return hasSelection;
                            }

                            // Matrix questions are ALWAYS required - must fill all rows
                            if (currentQuestion.questionType === 'matrix') {
                                if (vals.length === 0 || !vals[0]) {
                                    // console.log('  📊 Matrix (ALWAYS required): NO DATA ✗');
                                    return false;
                                }
                                try {
                                    const matrixData = JSON.parse(vals[0]);
                                    const rows =
                                        currentQuestion.questionConfig?.rows ||
                                        [];
                                    const allFilled = rows.every(
                                        (row: any) =>
                                            matrixData[getRowKey(row)] !==
                                            undefined,
                                    );
                                    // console.log('  📊 Matrix (ALWAYS required):', allFilled ? 'ALL ROWS FILLED ✓' : 'INCOMPLETE ✗',
                                    //     `(${Object.keys(matrixData).length}/${rows.length} rows)`);
                                    return allFilled;
                                } catch {
                                    // console.log('  📊 Matrix (ALWAYS required): PARSE ERROR ✗');
                                    return false;
                                }
                            }

                            // Email questions must have valid email format (ALWAYS validated, not optional)
                            if (currentQuestion.questionType === 'email') {
                                const emailValue = vals[0]?.trim() || '';
                                const hasEmail = emailValue !== '';

                                if (!hasEmail) {
                                    // console.log('    Email check: NO EMAIL ✗');
                                    return false;
                                }

                                // Validate email pattern
                                const pattern =
                                    currentQuestion.questionConfig
                                        ?.validationRules?.pattern;
                                if (pattern) {
                                    try {
                                        const regex = new RegExp(pattern);
                                        const isValid = regex.test(emailValue);
                                        // console.log('    Email check (custom pattern):', isValid ? 'VALID ✓' : 'INVALID ✗');
                                        return isValid;
                                    } catch (e) {
                                        console.error(
                                            'Invalid email regex pattern:',
                                            pattern,
                                        );
                                    }
                                }

                                // Fallback to basic email validation
                                const basicEmailRegex =
                                    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                                const isValid =
                                    basicEmailRegex.test(emailValue);
                                // console.log('    Email check (basic pattern):', isValid ? 'VALID ✓' : 'INVALID ✗');
                                return isValid;
                            }

                            // Date questions - validate date is within min/max range
                            if (currentQuestion.questionType === 'date') {
                                const dateValue = vals[0]?.trim() || '';

                                if (!dateValue) {
                                    // console.log('  📅 Date check: NO DATE ✗');
                                    return false;
                                }

                                // Import dayjs for date validation
                                const dayjs = require('dayjs');
                                const selectedDate = dayjs(dateValue);

                                if (!selectedDate.isValid()) {
                                    // console.log('  📅 Date check: INVALID DATE ✗');
                                    return false;
                                }

                                // Check if weekends are disabled
                                if (
                                    currentQuestion.questionConfig
                                        ?.disableWeekends
                                ) {
                                    const day = selectedDate.day();
                                    if (day === 0 || day === 6) {
                                        // console.log('  📅 Date check: WEEKEND NOT ALLOWED ✗');
                                        return false;
                                    }
                                }

                                // Determine effective minDate
                                let minDate =
                                    currentQuestion.questionConfig?.minDate;
                                if (
                                    !minDate &&
                                    currentQuestion.questionConfig
                                        ?.disablePastDates
                                ) {
                                    minDate = dayjs().format('YYYY-MM-DD');
                                }

                                // Check minDate constraint
                                if (minDate) {
                                    const minDateObj = dayjs(minDate);
                                    if (
                                        selectedDate.isBefore(minDateObj, 'day')
                                    ) {
                                        // console.log('  📅 Date check: BEFORE MIN DATE ✗');
                                        return false;
                                    }
                                }

                                // Determine effective maxDate
                                let maxDate =
                                    currentQuestion.questionConfig?.maxDate;
                                if (
                                    !maxDate &&
                                    currentQuestion.questionConfig
                                        ?.disableFutureDates
                                ) {
                                    maxDate = dayjs().format('YYYY-MM-DD');
                                }

                                // Check maxDate constraint
                                if (maxDate) {
                                    const maxDateObj = dayjs(maxDate);
                                    if (
                                        selectedDate.isAfter(maxDateObj, 'day')
                                    ) {
                                        // console.log('  📅 Date check: AFTER MAX DATE ✗');
                                        return false;
                                    }
                                }

                                // console.log('  📅 Date check: VALID ✓');
                                return true;
                            }

                            // For other question types, check if required
                            if (!isRequired) {
                                // console.log('  ✓ Question is OPTIONAL - allowing next without answer');
                                return true;
                            }

                            // console.log('  ⚠️  Question is REQUIRED - checking answer validity');

                            // REQUIRED questions validation below:

                            // Text questions must have non-empty value and meet length requirements
                            if (currentQuestion.questionType === 'text') {
                                const textValue = vals[0]?.trim() || '';
                                const hasText = textValue !== '';

                                if (!hasText) {
                                    // console.log('    Text check: NO TEXT ✗');
                                    return false;
                                }

                                // Check minLength validation (only if > 0)
                                const minLength =
                                    currentQuestion.questionConfig?.minLength ||
                                    0;
                                if (
                                    minLength > 0 &&
                                    textValue.length < minLength
                                ) {
                                    // console.log('    Text check: TOO SHORT ✗', `(${textValue.length}/${minLength})`);
                                    return false;
                                }

                                // Check maxLength validation (should not exceed if specified)
                                const maxLength =
                                    currentQuestion.questionConfig?.maxLength;
                                if (maxLength && textValue.length > maxLength) {
                                    // console.log('    Text check: TOO LONG ✗', `(${textValue.length}/${maxLength})`);
                                    return false;
                                }

                                // console.log('    Text check: VALID ✓', `(${textValue.length} chars)`);
                                return true;
                            }

                            // Checkbox questions - check minSelections (default 0 for required questions means at least 1)
                            if (currentQuestion.questionType === 'checkbox') {
                                const minSelections =
                                    currentQuestion.questionConfig
                                        ?.minSelections ?? 1; // Required checkbox needs at least 1
                                const isValid = vals.length >= minSelections;
                                // console.log('    Checkbox check:', isValid, 'selected:', vals.length, 'min:', minSelections);
                                return isValid;
                            }

                            // Multiselect must have at least one selection if required
                            const hasSelection = vals.length > 0;
                            // console.log('    Multiselect check:', hasSelection);
                            return hasSelection;
                        })();

                        // console.log('  → Final isAnswered:', isAnswered, '| Button:', isAnswered ? 'ENABLED ✓' : 'DISABLED ✗');

                        return (
                            <button
                                id="footer-button"
                                disabled={!isAnswered || isProcessingNext}
                                aria-disabled={!isAnswered || isProcessingNext}
                                onClick={
                                    isAnswered && !isProcessingNext
                                        ? onNextQuestion
                                        : undefined
                                }
                                onKeyDown={(e) => {
                                    if (
                                        e.key === 'Enter' &&
                                        isAnswered &&
                                        !isProcessingNext
                                    ) {
                                        e.preventDefault();
                                        onNextQuestion();
                                    }
                                }}
                                className={[
                                    // preserve existing styling from id selector, only adjust state styles
                                    !isAnswered || isProcessingNext
                                        ? 'opacity-50 cursor-not-allowed'
                                        : 'opacity-100',
                                ].join(' ')}
                            >
                                {isProcessingNext ? (
                                    <div className="flex items-center gap-2">
                                        <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                        <p className="text-[16px]">
                                            {t(
                                                'survey_form.category_page.processing',
                                            )}
                                        </p>
                                    </div>
                                ) : (
                                    <p className="text-[16px]">
                                        {t('survey_form.category_page.next')}
                                    </p>
                                )}
                            </button>
                        );
                    })()}
                </div>
            )}

            {!hasResumeOverlay && shouldShowEditIntroSkipButton && (
                <div
                    ref={footerRef}
                    id="footer-button-container"
                    className="flex items-center gap-4"
                >
                    <button
                        type="button"
                        onClick={onSkipOptionalCategory}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                onSkipOptionalCategory();
                            }
                        }}
                        className="flex h-[56px] px-6 justify-center items-center rounded-full border border-[#2A807B] bg-white text-[#2A807B] font-semibold"
                    >
                        {t(
                            'survey_form.category_page.skip_to_result',
                            'ข้ามไปดูผลลัพธ์ของคุณ',
                        )}
                    </button>
                    <button
                        id="footer-button"
                        onClick={onStartCategory}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                void onStartCategory();
                            }
                        }}
                    >
                        <p className="text-[16px]">
                            {categoryIntroPrimaryButtonText}
                        </p>
                    </button>
                </div>
            )}

            {!hasResumeOverlay && isOptionalCategoryIntro && (
                <div
                    ref={footerRef}
                    id="footer-button-container"
                    className="flex items-center gap-4"
                >
                    {(!isAnsweredOptionalCategoryIntro ||
                        shouldShowOptionalSkipButtonForRevisit) && (
                        <button
                            type="button"
                            onClick={onSkipOptionalCategory}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    onSkipOptionalCategory();
                                }
                            }}
                            className="flex h-[56px] px-6 justify-center items-center rounded-full border border-[#2A807B] bg-white text-[#2A807B] font-semibold"
                        >
                            {t(
                                'survey_form.category_page.skip_to_result',
                                'ข้ามไปดูผลลัพธ์ของคุณ',
                            )}
                        </button>
                    )}
                    <button
                        id="footer-button"
                        onClick={handleOptionalPrimaryAction}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                void handleOptionalPrimaryAction();
                            }
                        }}
                    >
                        <p className="text-[16px]">
                            {optionalPrimaryButtonText}
                        </p>
                    </button>
                </div>
            )}

            {/* Footer for non-last category result mode */}
            {!hasResumeOverlay &&
                !workflowResult &&
                !showShareButtons &&
                mode === 'result' && (
                    <div
                        id="footer-button-container"
                        className="flex items-center gap-4"
                    >
                        {/* Back button */}
                        <button
                            id="footer-back-button"
                            type="button"
                            disabled={loadingWorkflowResult}
                            aria-disabled={loadingWorkflowResult}
                            onClick={() => {
                                if (history.length > 0) {
                                    goBack();
                                } else {
                                    // Go back to category intro
                                    setMode('categoryIntro');
                                    setStepIndex(0);
                                }
                            }}
                            aria-label={t('survey_form.category_page.back')}
                            className="flex flex-row items-center justify-center gap-2"
                        >
                            <WestIcon
                                style={{
                                    color: '#2A807B',
                                    width: '18px',
                                    height: '18px',
                                }}
                            />
                            <p className="text-[16px] text-[#2A807B] font-semibold">
                                {t('survey_form.category_page.back')}
                            </p>
                        </button>

                        {/* Next button to continue to next category */}
                        <button
                            id="footer-button"
                            disabled={loadingWorkflowResult}
                            aria-disabled={loadingWorkflowResult}
                            onClick={onNextFromResult}
                            onKeyDown={(e) => {
                                if (
                                    e.key === 'Enter' &&
                                    !loadingWorkflowResult
                                ) {
                                    e.preventDefault();
                                    onNextFromResult();
                                }
                            }}
                            className="opacity-100"
                        >
                            <p className="text-[16px]">
                                {t('survey_form.category_page.next_category')}
                            </p>
                        </button>
                    </div>
                )}

            {!hasResumeOverlay && showShareButtons && mode === 'result' && (
                <div
                    id="footer-button-container"
                    className="w-full items-center justify-center flex flex-col py-4"
                >
                    <button
                        id="footer-button"
                        className="!w-[240px]"
                        disabled={
                            isNavigatingToResult || isFinalInsightsLoading
                        }
                        aria-disabled={
                            isNavigatingToResult || isFinalInsightsLoading
                        }
                        onClick={navigateToSpecificResult}
                        onKeyDown={async (e) => {
                            if (
                                e.key === 'Enter' &&
                                !isNavigatingToResult &&
                                !isFinalInsightsLoading
                            ) {
                                e.preventDefault();
                                setIsNavigatingToResult(true);

                                // Mark survey as completed before navigating to specific-result page
                                const currentUserId =
                                    effectiveUserId ||
                                    props.userId ||
                                    createdGuestId;
                                if (currentUserId) {
                                    await markSurveyAsCompleted(
                                        props.slug,
                                        currentUserId,
                                    );
                                    console.log(
                                        '✅ Survey marked as completed before viewing specific result',
                                    );
                                }

                                const resultPath = props.lang
                                    ? `/${props.lang}/survey-form/${props.slug}/specific-result`
                                    : `/survey-form/${props.slug}/specific-result`;
                                router.push(resultPath);
                            }
                        }}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            className="inline-block mr-2 align-middle"
                        >
                            <path
                                d="M13.4456 4.33266C14.244 4.33266 14.8913 3.68543 14.8913 2.88703C14.8913 2.08864 14.244 1.44141 13.4456 1.44141C12.6472 1.44141 12 2.08864 12 2.88703C12 3.68543 12.6472 4.33266 13.4456 4.33266Z"
                                fill="white"
                            />
                            <path
                                d="M16.3373 2.31336C16.9762 2.31336 17.494 1.7955 17.494 1.15668C17.494 0.517863 16.9762 0 16.3373 0C15.6985 0 15.1807 0.517863 15.1807 1.15668C15.1807 1.7955 15.6985 2.31336 16.3373 2.31336Z"
                                fill="white"
                            />
                            <path
                                d="M21.1952 19.7323L15.1806 10.1152V7.22709H16.9156V5.20312H7.08428V7.22709H8.8193V10.1152L2.80465 19.7323C2.58414 20.1411 2.45801 20.6085 2.45801 21.1064C2.45801 22.7032 3.75245 23.9981 5.34971 23.9981H18.6506C20.2474 23.9981 21.5423 22.7036 21.5423 21.1064C21.5423 20.6085 21.4162 20.1411 21.1952 19.7323ZM7.40901 16.1871L10.8433 10.6958V7.22664H13.1566V10.6958L16.5909 16.1871H7.40901Z"
                                fill="white"
                            />
                        </svg>
                        {isNavigatingToResult ? (
                            <div className="inline-flex items-center gap-2 align-middle">
                                <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                <p className="inline-block">
                                    {t('survey_form.category_page.loading')}
                                </p>
                            </div>
                        ) : (
                            <p className="inline-block align-middle">
                                {t('survey_form.category_page.view_formula')}
                            </p>
                        )}
                    </button>
                </div>
            )}
            {/*------------------footer---------------------*/}

            {isMobile !== false && (
                <ShareBottomSheet
                    isOpen={isShareModalOpen}
                    onClose={() => setIsShareModalOpen(false)}
                    imageUrl={getOgImageUrl()}
                    shareUrl={shareUrl || ''}
                    onDownloadImage={handleDownloadImage}
                    onCopyLink={handleCopyLink}
                    personaLabel={personaLabel}
                />
            )}
        </div>
    );
};

export default CategoryPage;
